import Editor from "@toast-ui/editor";
import "@toast-ui/editor/dist/i18n/fr-fr";
import Chart from "chart.js/auto";
import type { AnalyticsEvent } from "../services/supabase";
import type { GeneratedArticleContent } from "../services/openai-article-generator";
import { DEFAULT_SOCIAL_NETWORKS, SOCIAL_NETWORKS } from "../domain/social-networks";
import { createCategoryId } from "../domain/category";
import { isValidSlug } from "../domain/slug";
import {
  ARTICLE_WORD_COUNT_MAX,
  ARTICLE_WORD_COUNT_MIN,
  ARTICLE_WORD_COUNT_TARGET,
  countMarkdownWords,
  isArticleWordCountRecommended
} from "../domain/markdown";
import { DEFAULT_ARTICLE_LOCALE, EUROPEAN_LOCALES, isEuropeanLocale } from "../domain/locales";
import { isSiteThemeId, SITE_THEMES } from "../domain/themes";
import {
  ARTICLE_TITLE_IDEAL_LENGTH,
  ARTICLE_TITLE_MAX_LENGTH,
  ARTICLE_TITLE_MIN_LENGTH,
  DEFAULT_SITE_SEO_DESCRIPTION,
  SEO_DESCRIPTION_MAX_LENGTH,
  SEO_DESCRIPTION_MIN_LENGTH
} from "../domain/types";
import { uniquePublishProfileId } from "../domain/publish-profile";
import { PASSWORD_MIN_LENGTH, newPasswordError, passwordChangeError } from "../domain/password";

type ArticleStatus = "draft" | "published" | "scheduled" | "archived";

interface Category {
  id: string;
  name: string;
  slug: string;
  order: number;
  enabled: boolean;
  aliases?: string[];
  archivedAt?: string;
}

function datetimeLocalValue(value?: string): string {
  const date = value ? new Date(value) : new Date();
  if (!Number.isFinite(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function renderFeaturedArticlePanel(article: ArticleRecord): string {
  const settings = state.featuredArticleSettings;
  const selected = settings.articleId === article.id;
  const featuredArticle = settings.articleId
    ? state.articles.find((candidate) => candidate.id === settings.articleId)
    : undefined;
  const draft = state.featuredArticleDraft?.articleId === article.id
    ? state.featuredArticleDraft
    : undefined;
  const now = Date.now();
  const startsAt = settings.startsAt ? new Date(settings.startsAt).getTime() : undefined;
  const endsAt = settings.endsAt ? new Date(settings.endsAt).getTime() : undefined;
  const stateLabel = selected
    ? startsAt && startsAt > now
      ? `Programmée à partir du ${new Date(settings.startsAt!).toLocaleString("fr-FR")}`
      : endsAt && endsAt <= now
        ? `Terminée le ${new Date(settings.endsAt!).toLocaleString("fr-FR")}`
        : settings.endsAt
          ? `À la une jusqu’au ${new Date(settings.endsAt).toLocaleString("fr-FR")}`
          : "À la une jusqu’au retrait manuel"
    : featuredArticle
      ? `Une actuelle : ${featuredArticle.frontMatter.title}`
      : settings.articleId
        ? "Une configurée sur un article indisponible"
        : "Aucun article explicitement mis à la une";
  const eligible = article.frontMatter.status === "published" || article.frontMatter.status === "scheduled";

  return `
    <section class="featured-editor" aria-labelledby="featured-editor-title">
      <div>
        <h3 id="featured-editor-title">Article à la une</h3>
        <p>${escapeHtml(stateLabel)}</p>
      </div>
      <div class="row two">
        <label>
          Début
          <input type="datetime-local" data-featured-field="startsAt" value="${escapeHtml(draft?.startsAt ?? datetimeLocalValue(selected ? settings.startsAt : undefined))}">
        </label>
        <label>
          Fin facultative
          <input type="datetime-local" data-featured-field="endsAt" value="${escapeHtml(draft?.endsAt ?? (selected && settings.endsAt ? datetimeLocalValue(settings.endsAt) : ""))}">
          <span class="field-hint">Laissez vide pour conserver la une jusqu’au retrait manuel.</span>
        </label>
      </div>
      ${eligible ? "" : '<p class="status warn">Enregistrez d’abord cet article avec le statut publié ou programmé.</p>'}
      ${state.automaticPublishSettings.enabled
        ? ""
        : '<p class="status warn">La publication automatique est désactivée : les changements seront visibles après la prochaine publication manuelle.</p>'}
      <div class="actions">
        <button type="button" data-action="save-featured-article"${!eligible || state.featuredArticleBusy ? " disabled" : ""}>
          ${state.featuredArticleBusy ? "Enregistrement…" : selected ? "Mettre à jour la durée" : "Mettre cet article à la une"}
        </button>
        ${selected ? `<button type="button" data-action="remove-featured-article"${state.featuredArticleBusy ? " disabled" : ""}>Retirer de la une</button>` : ""}
      </div>
    </section>
  `;
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} o`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} Ko`;
  return `${(value / (1024 * 1024)).toFixed(1)} Mo`;
}

async function saveFeaturedArticle(articleId?: string): Promise<void> {
  if (state.featuredArticleBusy) return;
  const startsAtValue = app.querySelector<HTMLInputElement>('[data-featured-field="startsAt"]')?.value || "";
  const endsAtValue = app.querySelector<HTMLInputElement>('[data-featured-field="endsAt"]')?.value || "";
  state.featuredArticleBusy = true;
  state.saveError = undefined;
  render();
  try {
    let settings: FeaturedArticleSettings = { configured: true };
    if (articleId) {
      if (!startsAtValue) throw new Error("La date de début de mise en avant est obligatoire.");
      settings = {
        configured: true,
        articleId,
        startsAt: new Date(startsAtValue).toISOString(),
        endsAt: endsAtValue ? new Date(endsAtValue).toISOString() : undefined
      };
    }
    state.featuredArticleSettings = await window.lesBoulesStudio.saveFeaturedArticleSettings(settings);
    state.featuredArticleDraft = undefined;
    showSaveNotice(articleId ? "Mise en avant enregistrée" : "Article retiré de la une");
  } catch (error) {
    state.saveError = error instanceof Error ? error.message : String(error);
  } finally {
    state.featuredArticleBusy = false;
    render();
  }
}

interface SiteConfig {
  name: string;
  url: string;
  language: string;
  tagline: string;
  seoDescription?: string;
  author: string;
  articlesPerPage: number;
  timezone: string;
  template: string;
  disabledThemes?: string[];
  analyticsEnabled?: boolean;
  analyticsProvider?: "supabase" | "none";
  showViewCounter?: boolean;
  supabaseUrl?: string;
  supabaseAnonKey?: string;
  socialSharingEnabled?: boolean;
  socialNetworks?: string[];
  googleAnalyticsEnabled?: boolean;
  googleAnalyticsId?: string;
  contentMode?: "local" | "supabase";
  supabaseWorkspaceId?: string;
}

interface ArticleFrontMatter {
  editorialId: string;
  title: string;
  slug: string;
  date: string;
  updated: string;
  categoryId: string;
  author: string;
  status: ArticleStatus;
  featured: boolean;
  image?: string;
  image_alt?: string;
  tags: string[];
  satire: boolean;
  seo_title?: string;
  seo_description?: string;
  locale?: string;
  aliases?: string[];
}

interface ArticleRecord {
  id: string;
  filePath: string;
  frontMatter: ArticleFrontMatter;
  body: string;
  excerpt: string;
  version?: number;
}

interface FeaturedArticleSettings {
  configured: boolean;
  articleId?: string;
  startsAt?: string;
  endsAt?: string;
  updatedAt?: string;
}

interface PageFrontMatter {
  id: string;
  title: string;
  slug: string;
  order?: number;
  seo_title?: string;
  seo_description?: string;
  aliases?: string[];
}

interface PageRecord {
  id: string;
  filePath: string;
  frontMatter: PageFrontMatter;
  body: string;
  excerpt: string;
  version?: number;
}

interface MediaAssetRecord {
  id: string;
  publicPath: string;
  fileName: string;
  sizeBytes: number;
  createdAt: string;
  previewUrl?: string;
  width?: number;
  height?: number;
  variantPublicPaths?: string[];
  optimized?: boolean;
  sourceAvailable?: boolean;
}

interface MediaOrphan {
  kind: "missing_storage" | "missing_metadata";
  mediaAssetId?: string;
  storagePath: string;
  publicPath: string;
}

type PublishProtocol = "sftp" | "ftps" | "ftp";
type PublishMode = "passive" | "active";

interface PublishProfile {
  id: string;
  name: string;
  protocol: PublishProtocol;
  host: string;
  port: number;
  username: string;
  remotePath: string;
  publicUrl: string;
  timeout: number;
  passiveMode?: PublishMode;
  sshFingerprint?: string;
  sshKeyPath?: string;
}

interface PublishConfig {
  profiles: PublishProfile[];
  htaccessEnabled?: boolean;
  htaccessContent?: string;
}

interface AutomaticPublishSettings {
  enabled: boolean;
  profileId: string;
  secretConfigured: boolean;
  secretUpdatedAt?: string;
  workerActive: boolean;
  workerLastSeenAt?: string;
}

interface ScheduledPublishJob {
  id: string;
  profileId: string;
  triggerArticleId?: string;
  scheduledFor: string;
  nextAttemptAt: string;
  status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  attempts: number;
  maxAttempts: number;
  leaseExpiresAt?: string;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
  createdAt: string;
}

interface PreflightIssue {
  severity: "error" | "warning" | "info";
  scope: "site" | "article" | "page" | "media" | "publishing" | "generated";
  id: string;
  message: string;
  resourceId?: string;
  resourceLabel?: string;
}

interface PreflightReport {
  generatedAt: string;
  errors: number;
  warnings: number;
  passedChecks: number;
  canPublish: boolean;
  issues: PreflightIssue[];
}

interface PublicationManifestFile {
  path: string;
  sha256: string;
  sizeBytes: number;
}

interface PublicationPlan {
  profileId: string;
  generatedAt: string;
  upload: PublicationManifestFile[];
  unchanged: PublicationManifestFile[];
  remove: PublicationManifestFile[];
}

interface PublicationReport {
  id: string;
  profileId: string;
  publishedAt: string;
  protocol: PublishProtocol;
  host: string;
  remotePath: string;
  uploadedFiles: number;
  removedFiles: number;
  unchangedFiles: number;
  snapshotPath: string;
  restoredFrom?: string;
}

interface ProgressNotice {
  title: string;
  caption: string;
  durationMs: number;
}

type AnalyticsRange = "7d" | "30d" | "all";

interface AnalyticsSummary {
  total_views: number;
  unique_visitors: number;
  total_articles: number;
  articles: Array<{
    slug: string;
    title: string;
    views: number;
    unique_visitors: number;
    last_view: string;
  }>;
  timeline: Array<{
    date: string;
    count: number;
  }>;
}

interface ConnectionStatus {
  ok: boolean;
  message: string;
}

interface PushStatus {
  ok: boolean;
  message: string;
}

type TeamRole = "admin" | "editor" | "author" | "viewer";

interface TeamMember {
  userId: string;
  email: string;
  displayName: string;
  role: TeamRole;
  createdAt: string;
  currentUser: boolean;
}

interface StudioAuthState {
  configured: boolean;
  contentMode: "local" | "supabase";
  authenticated: boolean;
  passwordRecovery?: boolean;
  email?: string;
  workspaceId?: string;
  workspaces: Array<{ id: string; name: string; slug: string; role: string }>;
}

interface OpenAISettingsState {
  configured: boolean;
  model: string;
  prompt: string;
  defaultPrompt: string;
}

type ArticleSortColumn = "status" | "date" | "title" | "category" | "updated";
type SortDirection = "asc" | "desc";

declare global {
  interface Window {
    lesBoulesStudio: {
      getAuthState(): Promise<StudioAuthState>;
      signIn(email: string, password: string): Promise<StudioAuthState>;
      signOut(): Promise<StudioAuthState>;
      requestPasswordReset(email: string): Promise<void>;
      changePassword(currentPassword: string, newPassword: string): Promise<void>;
      completePasswordRecovery(newPassword: string): Promise<StudioAuthState>;
      createWorkspace(name: string, slug: string): Promise<string>;
      updateWorkspace(name: string, slug: string): Promise<void>;
      selectWorkspace(workspaceId: string): Promise<StudioAuthState>;
      listTeamMembers(): Promise<TeamMember[]>;
      inviteTeamMember(input: { email: string; displayName?: string; role: TeamRole }): Promise<TeamMember[]>;
      sendTeamMemberPasswordReset(userId: string): Promise<void>;
      updateTeamMemberRole(userId: string, role: TeamRole): Promise<TeamMember[]>;
      removeTeamMember(userId: string): Promise<TeamMember[]>;
      getConfig(): Promise<SiteConfig>;
      saveConfig(config: SiteConfig): Promise<SiteConfig>;
      getOpenAISettings(): Promise<OpenAISettingsState>;
      saveOpenAISettings(input: { apiKey?: string; model?: string; prompt?: string; clearApiKey?: boolean }): Promise<OpenAISettingsState>;
      generateArticleWithAI(theme: string, prompt: string): Promise<GeneratedArticleContent>;
      listAnalyticsEvents(): Promise<AnalyticsEvent[]>;
      testAnalyticsConnection(): Promise<{ ok: boolean; count: number }>;
      listCategories(): Promise<Category[]>;
      saveCategory(category: Category): Promise<Category[]>;
      deleteCategory(id: string): Promise<Category[]>;
      listPages(): Promise<PageRecord[]>;
      listArticles(): Promise<ArticleRecord[]>;
      getFeaturedArticleSettings(): Promise<FeaturedArticleSettings>;
      saveFeaturedArticleSettings(settings: FeaturedArticleSettings): Promise<FeaturedArticleSettings>;
      getArticle(id: string): Promise<ArticleRecord | undefined>;
      createArticle(): Promise<ArticleRecord>;
      saveArticle(article: ArticleRecord): Promise<ArticleRecord>;
      duplicateArticle(id: string): Promise<ArticleRecord>;
      archiveArticle(id: string): Promise<ArticleRecord>;
      deleteArticle(id: string): Promise<boolean>;
      createPage(): Promise<PageRecord>;
      savePage(page: PageRecord): Promise<PageRecord>;
      deletePage(id: string): Promise<boolean>;
      saveMediaAsset(fileName: string, bytes: Uint8Array, contentType?: string): Promise<string>;
      listMediaAssets(): Promise<MediaAssetRecord[]>;
      findMediaOrphans(): Promise<MediaOrphan[]>;
      deleteMediaAsset(id: string): Promise<boolean>;
      deleteMediaOrphan(orphan: MediaOrphan): Promise<boolean>;
      getPublishConfig(): Promise<PublishConfig>;
      savePublishConfig(config: PublishConfig): Promise<PublishConfig>;
      getAutomaticPublishSettings(): Promise<AutomaticPublishSettings>;
      saveAutomaticPublishSettings(input: {
        enabled: boolean;
        profileId: string;
        password?: string;
        passphrase?: string;
      }): Promise<AutomaticPublishSettings>;
      deleteAutomaticPublishSecret(profileId: string): Promise<void>;
      listScheduledPublishJobs(): Promise<ScheduledPublishJob[]>;
      retryScheduledPublishJob(id: string): Promise<void>;
      runPreflight(profileId?: string): Promise<PreflightReport>;
      planPublishProfile(payload: { profileId: string; secrets: { password?: string; passphrase?: string } }): Promise<PublicationPlan>;
      pushPublishProfile(payload: { profileId: string; secrets: { password?: string; passphrase?: string }; confirmWarnings?: boolean }): Promise<PublicationReport>;
      listPublicationHistory(profileId: string): Promise<PublicationReport[]>;
      restorePublication(payload: { profileId: string; publicationId: string; secrets: { password?: string; passphrase?: string } }): Promise<PublicationReport>;
      buildSite(): Promise<unknown>;
      startPreview(): Promise<string>;
      openMarkdownPreview(title: string, html: string): Promise<boolean>;
      stopPreview(): Promise<boolean>;
      previewStatus(): Promise<{ running: boolean; url?: string }>;
      openExternal(url: string): Promise<boolean>;
      pickFolder(): Promise<string | undefined>;
      onContentChanged(callback: () => void): () => void;
    };
  }
}

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) {
  throw new Error("Application root missing");
}

const state = {
  auth: undefined as StudioAuthState | undefined,
  teamStatus: undefined as { ok: boolean; message: string } | undefined,
  passwordResetDialogOpen: false,
  passwordResetStatus: undefined as { ok: boolean; message: string } | undefined,
  suppressLoginAutofill: false,
  teamMembers: [] as TeamMember[],
  teamLoading: false,
  config: undefined as SiteConfig | undefined,
  categories: [] as Category[],
  pages: [] as PageRecord[],
  articles: [] as ArticleRecord[],
  featuredArticleSettings: { configured: false } as FeaturedArticleSettings,
  featuredArticleBusy: false,
  featuredArticleDraft: undefined as { articleId: string; startsAt: string; endsAt: string } | undefined,
  mediaAssets: [] as MediaAssetRecord[],
  mediaOrphans: [] as MediaOrphan[],
  mediaStatus: undefined as ConnectionStatus | undefined,
  mediaLoading: false,
  articleMediaStatus: undefined as ConnectionStatus | undefined,
  articleMediaLoading: false,
  publishConfig: { profiles: [] } as PublishConfig,
  automaticPublishSettings: { enabled: false, profileId: "", secretConfigured: false, workerActive: false } as AutomaticPublishSettings,
  scheduledPublishJobs: [] as ScheduledPublishJob[],
  publishBusy: false,
  articleSaving: false,
  selectedPublishProfileId: undefined as string | undefined,
  selectedArticleId: undefined as string | undefined,
  selectedPageId: undefined as string | undefined,
  activeMenu: "dashboard" as string,
  previewUrl: undefined as string | undefined,
  buildStatus: "Prêt" as string,
  saveNotice: undefined as string | undefined,
  saveError: undefined as string | undefined,
  progressNotice: undefined as ProgressNotice | undefined,
  editorMode: "wysiwyg" as "wysiwyg" | "markdown",
  dirty: false,
  sidebarCollapsed: false,
  settingsLauncherOpen: false,
  siteCreationOpen: false,
  articleListCollapsed: false,
  articleEditorCollapsed: false,
  filter: "",
  sortColumn: "date" as ArticleSortColumn,
  sortDirection: "desc" as SortDirection,
  categoryFilter: "all",
  statusFilter: "all",
  analyticsEvents: [] as AnalyticsEvent[],
  analyticsRange: "30d" as AnalyticsRange,
  analyticsQuery: "",
  analyticsData: undefined as AnalyticsSummary | undefined,
  supabaseConnectionStatus: undefined as ConnectionStatus | undefined,
  openAISettings: { configured: false, model: "gpt-4.1-mini", prompt: "", defaultPrompt: "" } as OpenAISettingsState,
  openAIStatus: undefined as ConnectionStatus | undefined,
  aiDialogOpen: false,
  aiTheme: "",
  aiGenerating: false,
  aiGenerationStatus: undefined as ConnectionStatus | undefined,
  aiGeneratedArticleId: undefined as string | undefined,
  aiGenerationNote: undefined as string | undefined,
  aiAlternativeTitles: [] as string[],
  pushDialogOpen: false,
  pushPassword: "",
  pushPassphrase: "",
  pushRunning: false,
  pushStatus: undefined as PushStatus | undefined,
  preflightReport: undefined as PreflightReport | undefined,
  publicationPlan: undefined as PublicationPlan | undefined,
  publicationHistory: [] as PublicationReport[]
};

let saveNoticeTimer: number | undefined;
let realtimeRefreshTimer: number | undefined;
let articleBodyEditor: Editor | undefined;
let pageBodyEditor: Editor | undefined;
let analyticsChart: unknown;

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function workspaceDisplayName(workspace: StudioAuthState["workspaces"][number]): string {
  return workspace.name.trim() || workspace.slug.trim() || "Site sans nom";
}

function teamRoleLabel(role: string): string {
  return {
    admin: "Administrateur",
    editor: "Éditeur",
    author: "Auteur",
    viewer: "Lecteur"
  }[role] || role;
}

function normalizeImagePath(pathValue?: string): string {
  if (!pathValue) {
    return "/assets/img/favicon.svg";
  }
  return `/${pathValue.replace(/^\/+/, "")}`;
}

function markdownToHtml(markdown: string): string {
  const escaped = escapeHtml(markdown)
    .replace(/\r?\n\r?\n/g, "\n\n")
    .replace(/^### (.*)$/gm, "<h3>$1</h3>")
    .replace(/^## (.*)$/gm, "<h2>$1</h2>")
    .replace(/^# (.*)$/gm, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/^\- (.*)$/gm, "<li>$1</li>");

  return escaped
    .split(/\n{2,}/)
    .map((block) => {
      if (block.startsWith("<h") || block.startsWith("<ul") || block.startsWith("<li")) {
        return block.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");
      }
      if (block.startsWith("<blockquote>")) {
        return block;
      }
      if (block.startsWith("<li>")) {
        return `<ul>${block}</ul>`;
      }
      return `<p>${block.replace(/\n/g, "<br>")}</p>`;
    })
    .join("");
}

function slugifyTitle(title: string): string {
  return title
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-+/g, "-");
}

function sortPages(pages: PageRecord[]): PageRecord[] {
  return [...pages].sort((a, b) => (a.frontMatter.order ?? 0) - (b.frontMatter.order ?? 0) || a.frontMatter.slug.localeCompare(b.frontMatter.slug));
}

function sortPublishProfiles(profiles: PublishProfile[]): PublishProfile[] {
  return [...profiles].sort((a, b) => a.name.localeCompare(b.name));
}

function defaultPublishProfile(): PublishProfile {
  return {
    id: "production",
    name: "Production",
    protocol: "sftp",
    host: "",
    port: 22,
    username: "",
    remotePath: "/www/",
    publicUrl: "",
    timeout: 30,
    passiveMode: "passive",
    sshFingerprint: "",
    sshKeyPath: ""
  };
}

function getAnalyticsCutoff(range: AnalyticsRange): Date | null {
  if (range === "all") {
    return null;
  }

  const days = range === "7d" ? 7 : 30;
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000);
}

function filterAnalyticsEvents(events: AnalyticsEvent[], range: AnalyticsRange, query: string): AnalyticsEvent[] {
  const cutoff = getAnalyticsCutoff(range);
  const normalizedQuery = query.trim().toLowerCase();
  return events.filter((event) => {
    const viewedAt = new Date(event.viewed_at);
    const matchesRange = !cutoff || viewedAt >= cutoff;
    const haystack = [event.article_slug, event.article_title || "", event.referrer || "", event.device_type || ""]
      .join(" ")
      .toLowerCase();
    const matchesQuery = !normalizedQuery || haystack.includes(normalizedQuery);
    return matchesRange && matchesQuery;
  });
}

function summarizeAnalyticsEvents(events: AnalyticsEvent[]): AnalyticsSummary {
  const byArticle = new Map<string, {
    slug: string;
    title: string;
    views: number;
    uniqueVisitors: Set<string>;
    lastView: string;
  }>();

  const timeline = new Map<string, number>();

  events.forEach((event) => {
    const article = byArticle.get(event.article_id) || {
      articleId: event.article_id,
      slug: event.article_slug,
      title: event.article_title || event.article_slug,
      views: 0,
      uniqueVisitors: new Set<string>(),
      lastView: event.viewed_at
    };

    article.views += 1;
    article.uniqueVisitors.add(event.visitor_id);
    article.lastView = event.viewed_at;
    byArticle.set(event.article_id, article);

    const day = event.viewed_at.slice(0, 10);
    timeline.set(day, (timeline.get(day) || 0) + 1);
  });

  const timelineEntries = Array.from(timeline.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, count]) => ({ date, count }));

  return {
    total_views: events.length,
    unique_visitors: new Set(events.map((event) => event.visitor_id)).size,
    total_articles: byArticle.size,
    articles: Array.from(byArticle.values())
      .map((article) => ({
        slug: article.slug,
        title: article.title,
        views: article.views,
        unique_visitors: article.uniqueVisitors.size,
        last_view: article.lastView
      }))
      .sort((a, b) => b.views - a.views || b.last_view.localeCompare(a.last_view)),
    timeline: timelineEntries
  };
}

function escapeCsv(value: string): string {
  if (/[",\n;]/.test(value)) {
    return `"${value.replaceAll('"', '""')}"`;
  }
  return value;
}

function isPublicArticle(article: ArticleRecord): boolean {
  if (article.frontMatter.status === "archived" || article.frontMatter.status === "draft") {
    return false;
  }
  if (article.frontMatter.status === "scheduled") {
    return new Date(article.frontMatter.date).getTime() <= Date.now();
  }
  return true;
}

function sitePathsForPush(): string[] {
  const articles = state.articles
    .filter(isPublicArticle)
    .map((article) => `/articles/${article.frontMatter.slug}/`);
  const categories = state.categories
    .filter((category) => category.enabled)
    .map((category) => `/${category.slug}/`);
  const pages = sortPages(state.pages).map((page) => `/${page.frontMatter.slug}/`);

  return ["/", ...articles, ...categories, ...pages, "/rss.xml", "/sitemap.xml", "/robots.txt"];
}

function pushValidation(profile: PublishProfile | undefined): { valid: boolean; message?: string } {
  if (!profile) {
    return { valid: false, message: "Aucun profil de publication sélectionné." };
  }
  if (profile.protocol === "ftp") {
    return { valid: false, message: "FTP non chiffré est désactivé. Choisissez SFTP ou FTPS." };
  }
  if (!profile.host.trim()) {
    return { valid: false, message: "Serveur manquant." };
  }
  if (!profile.username.trim()) {
    return { valid: false, message: "Utilisateur manquant." };
  }
  if (!profile.remotePath.trim()) {
    return { valid: false, message: "Dossier distant manquant." };
  }
  if (profile.protocol === "sftp") {
    if (!profile.sshFingerprint?.trim()) {
      return { valid: false, message: "SFTP : empreinte SHA-256 de la clé hôte requise." };
    }
    if (!profile.sshKeyPath?.trim() && !state.pushPassword.trim()) {
      return { valid: false, message: "SFTP : mot de passe temporaire ou clé SSH requis." };
    }
    return { valid: true };
  }
  if (!state.pushPassword.trim()) {
    return { valid: false, message: "Mot de passe FTP/FTPS temporaire requis." };
  }
  return { valid: true };
}

function renderSiteTree(paths: string[]): string {
  const categoryPaths = paths.filter((path) => /^\/[^/]+\/$/.test(path) && !path.startsWith("/articles/") && path !== "/");
  const articlePaths = paths.filter((path) => path.startsWith("/articles/"));
  const filePaths = paths.filter((path) => path.endsWith(".xml") || path.endsWith(".txt"));

  return `
    <ul class="push-tree">
      <li><strong>/</strong></li>
      <li>articles
        <ul>
          ${articlePaths.map((path) => `<li>${escapeHtml(path.replace(/^\/articles\//, ""))}</li>`).join("") || "<li>—</li>"}
        </ul>
      </li>
      <li>rubriques
        <ul>
          ${categoryPaths.map((path) => `<li>${escapeHtml(path.replaceAll("/", ""))}</li>`).join("") || "<li>—</li>"}
        </ul>
      </li>
      <li>pages
        <ul>
          ${sortPages(state.pages).map((page) => `<li>${escapeHtml(page.frontMatter.slug)}/</li>`).join("") || "<li>—</li>"}
        </ul>
      </li>
      <li>fichiers
        <ul>
          ${filePaths.map((path) => `<li>${escapeHtml(path.replace(/^\//, ""))}</li>`).join("")}
        </ul>
      </li>
    </ul>
  `;
}

function renderPushDialog(): string {
  const profiles = sortPublishProfiles(state.publishConfig.profiles);
  const selectedProfile = currentPublishProfile() || profiles[0];
  const paths = sitePathsForPush();
  const validation = pushValidation(selectedProfile);

  return `
    <div class="app-notice-overlay push-dialog-overlay">
      <div class="push-dialog">
        <div class="push-dialog__header">
          <div>
            <h2>Préparer la mise en ligne</h2>
            <p class="status">Choisissez un profil FTP puis vérifiez le contenu qui sera poussé.</p>
          </div>
          <button type="button" class="push-dialog__close" data-action="close-push-dialog" aria-label="Fermer">×</button>
        </div>
        ${selectedProfile ? `
          <div class="editor">
            <div class="row two">
              <label>
                Connexion FTP
                <select data-push-profile-select>
                  ${profiles.map((profile) => `<option value="${escapeHtml(profile.id)}"${profile.id === selectedProfile.id ? " selected" : ""}>${escapeHtml(profile.name)}</option>`).join("")}
                </select>
              </label>
              <div class="panel">
                <h2>Récapitulatif</h2>
                <div class="push-summary">
                  <div><strong>Protocole</strong><span>${escapeHtml(selectedProfile.protocol.toUpperCase())}</span></div>
                  <div><strong>Serveur</strong><span>${escapeHtml(selectedProfile.host || "Non renseigné")}</span></div>
                  <div><strong>Port</strong><span>${selectedProfile.port}</span></div>
                  <div><strong>Utilisateur</strong><span>${escapeHtml(selectedProfile.username || "Non renseigné")}</span></div>
                  <div><strong>Dossier distant</strong><span>${escapeHtml(selectedProfile.remotePath || "/")}</span></div>
                  <div><strong>URL publique</strong><span>${escapeHtml(selectedProfile.publicUrl || "Non renseignée")}</span></div>
                </div>
              </div>
            </div>
            <div class="row two">
              <label>
                Mot de passe temporaire
                <input type="password" name="lbstudio-publish-password" data-push-secret="password" value="${escapeHtml(state.pushPassword)}" autocomplete="new-password" spellcheck="false" placeholder="${selectedProfile.protocol === "sftp" ? "Optionnel si clé SSH" : "Requis pour pousser"}">
              </label>
              <label>
                Passphrase clé SSH
                <input type="password" name="lbstudio-publish-passphrase" data-push-secret="passphrase" value="${escapeHtml(state.pushPassphrase)}" autocomplete="new-password" spellcheck="false" placeholder="Optionnel">
              </label>
            </div>
            <p class="status ${validation.valid ? "ok" : "warn"}" data-push-validation>${escapeHtml(validation.valid ? "Configuration prête pour la mise en ligne." : validation.message || "Configuration incomplète.")}</p>
            ${state.pushStatus ? `<p class="status ${state.pushStatus.ok ? "ok" : "err"}" data-push-operation-status>${escapeHtml(state.pushStatus.message)}</p>` : ""}
            <div class="panel">
              <h2>Contrôle avant publication</h2>
              ${state.preflightReport ? `
                <p class="status ${state.preflightReport.canPublish ? (state.preflightReport.warnings ? "warn" : "ok") : "err"}">
                  ${state.preflightReport.errors} erreur(s), ${state.preflightReport.warnings} avertissement(s), ${state.preflightReport.passedChecks} contrôle(s) réussi(s).
                </p>
                ${state.preflightReport.issues.length ? `
                  <ul class="push-check-list">
                    ${state.preflightReport.issues.map((issue) => `
                      <li class="${issue.severity === "error" ? "err" : "warn"}">
                        <strong>${issue.severity === "error" ? "Erreur" : "Avertissement"}</strong>
                        ${escapeHtml(issue.resourceLabel ? `${issue.resourceLabel} : ${issue.message}` : issue.message)}
                      </li>
                    `).join("")}
                  </ul>
                ` : `<p class="status ok">Aucun problème détecté.</p>`}
              ` : `<p class="status">Contrôle en attente.</p>`}
            </div>
            <div class="panel">
              <h2>Simulation différentielle</h2>
              ${state.publicationPlan ? `
                <div class="push-summary">
                  <div><strong>À envoyer</strong><span>${state.publicationPlan.upload.length}</span></div>
                  <div><strong>Inchangés</strong><span>${state.publicationPlan.unchanged.length}</span></div>
                  <div><strong>À supprimer</strong><span>${state.publicationPlan.remove.length}</span></div>
                </div>
                ${state.publicationPlan.remove.length ? `
                  <p class="status warn">Seuls ces fichiers, déjà suivis par le précédent manifeste LBStudio, seront supprimés :</p>
                  <ul class="push-check-list">
                    ${state.publicationPlan.remove.map((file) => `<li>${escapeHtml(file.path)}</li>`).join("")}
                  </ul>
                ` : `<p class="status ok">Aucun fichier distant suivi ne sera supprimé.</p>`}
              ` : `<p class="status">Lancez la simulation pour comparer le site au dernier manifeste distant.</p>`}
              <div class="actions">
                <button type="button"${validation.valid && !state.pushRunning ? "" : " disabled"} data-action="plan-push">Simuler la publication</button>
              </div>
            </div>
            <div class="row two">
              <div class="panel">
                <h2>Liste du site</h2>
                <div class="push-path-list">
                  <ul>
                    ${paths.map((path) => `<li>${escapeHtml(path)}</li>`).join("")}
                  </ul>
                </div>
              </div>
              <div class="panel">
                <h2>Arborescence du site</h2>
                <div class="push-path-list">
                  ${renderSiteTree(paths)}
                </div>
              </div>
            </div>
            <div class="panel">
              <h2>Historique et restauration</h2>
              ${state.publicationHistory.length ? `
                <ul class="push-check-list">
                  ${state.publicationHistory.map((report) => `
                    <li>
                      <strong>${escapeHtml(new Date(report.publishedAt).toLocaleString("fr-FR"))}</strong>
                      — ${report.uploadedFiles} envoyé(s), ${report.removedFiles} supprimé(s)
                      ${report.restoredFrom ? " — restauration" : ""}
                      <button type="button" data-action="restore-publication" data-publication-id="${escapeHtml(report.id)}"${state.pushRunning ? " disabled" : ""}>Restaurer</button>
                    </li>
                  `).join("")}
                </ul>
              ` : `<p class="status">Aucune publication sauvegardée pour ce profil.</p>`}
            </div>
            <div class="actions">
              <button type="button" class="primary"${validation.valid && state.preflightReport?.canPublish && !state.pushRunning ? "" : " disabled"} data-action="push-now">${state.pushRunning ? "Envoi en cours..." : "Pousser maintenant"}</button>
              <button type="button" data-action="close-push-dialog">Fermer</button>
            </div>
          </div>
        ` : `
          <div class="panel">
            <p class="status warn">Aucun profil FTP n’est encore configuré.</p>
            <div class="actions">
              <button type="button" data-action="close-push-dialog">Fermer</button>
            </div>
          </div>
        `}
      </div>
    </div>
  `;
}

function renderAIGenerationDialog(): string {
  return `
    <div class="app-notice-overlay push-dialog-overlay">
      <div class="push-dialog ai-generation-dialog">
        <div class="push-dialog__header">
          <div>
            <h2>Générer l’article avec OpenAI</h2>
            <p class="status">Indiquez le thème ou l’actualité. Le résultat restera un brouillon à relire.</p>
          </div>
          <button type="button" class="push-dialog__close" data-action="close-ai-dialog" aria-label="Fermer">×</button>
        </div>
        <div class="editor">
          <label>
            Thème
            <textarea class="ai-generation-theme" data-ai-theme placeholder="Exemple : la multiplication des candidatures à l’élection présidentielle">${escapeHtml(state.aiTheme)}</textarea>
          </label>
          <label>
            Prompt éditorial
            <textarea class="ai-generation-prompt" data-ai-prompt>${escapeHtml(state.openAISettings.prompt)}</textarea>
          </label>
          <p class="status">Ce prompt sera mémorisé sur cet appareil au lancement de la génération. Le format technique de la réponse reste contrôlé par le Studio.</p>
          ${state.aiGenerationStatus ? `<p class="status ${state.aiGenerationStatus.ok ? "ok" : "err"}">${escapeHtml(state.aiGenerationStatus.message)}</p>` : ""}
          <div class="actions">
            <button type="button" class="primary" data-action="generate-ai-article"${state.aiGenerating || !state.aiTheme.trim() || !state.openAISettings.prompt.trim() ? " disabled" : ""}>${state.aiGenerating ? "Génération en cours…" : "Générer le brouillon"}</button>
            <button type="button" data-action="reset-ai-prompt"${state.aiGenerating ? " disabled" : ""}>Restaurer le prompt d’origine</button>
            <button type="button" data-action="close-ai-dialog"${state.aiGenerating ? " disabled" : ""}>Annuler</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderPublishSettings(): string {
  const profiles = sortPublishProfiles(state.publishConfig.profiles);
  const selectedProfile = profiles.find((profile) => profile.id === state.selectedPublishProfileId) || profiles[0];
  if (!state.selectedPublishProfileId && selectedProfile) {
    state.selectedPublishProfileId = selectedProfile.id;
  }

  if (!selectedProfile) {
    return `
      <div class="editor">
        <div class="row two">
          <div class="panel">
            <h2>Profils</h2>
            <div class="actions">
              <button type="button" class="primary" data-action="publish-add-profile"${state.publishBusy ? " disabled" : ""}>Ajouter</button>
              <button type="button" disabled>Supprimer</button>
            </div>
            <p class="status">Aucun profil de publication configuré.</p>
          </div>
          <div class="panel">
            <h2>Publication</h2>
            <p>Ajoutez un profil pour configurer une destination SFTP ou FTPS.</p>
            ${renderSaveStatus()}
          </div>
        </div>
      </div>
    `;
  }

  const automaticProfileId = state.automaticPublishSettings.profileId || selectedProfile.id;
  const jobs = state.scheduledPublishJobs;
  return `
    <div class="editor">
      <div class="row two">
        <div class="panel">
          <h2>Profils</h2>
          <div class="actions">
            <button type="button" class="primary" data-action="publish-add-profile"${state.publishBusy ? " disabled" : ""}>Ajouter</button>
            <button type="button" data-action="publish-delete-profile"${state.publishBusy ? " disabled" : ""}>Supprimer</button>
          </div>
          <div class="publish-profiles">
            ${profiles
              .map((profile) => `<button type="button" data-publish-profile-id="${escapeHtml(profile.id)}"${profile.id === selectedProfile.id ? ' class="active"' : ""}>${escapeHtml(profile.name)}</button>`)
              .join("")}
          </div>
        </div>
        <div class="panel">
          <h2>${escapeHtml(selectedProfile.name)}</h2>
          <div class="row two">
            <label>Nom du profil <input data-publish-field="name" value="${escapeHtml(selectedProfile.name)}"></label>
            <label>Protocole
              <select data-publish-field="protocol">
                ${selectedProfile.protocol === "ftp" ? '<option value="ftp" selected disabled>FTP désactivé — choisissez un protocole sécurisé</option>' : ""}
                ${(["sftp", "ftps"] as const)
                  .map((protocol) => `<option value="${protocol}"${selectedProfile.protocol === protocol ? " selected" : ""}>${protocol.toUpperCase()}</option>`)
                  .join("")}
              </select>
            </label>
          </div>
          <div class="row two">
            <label>Serveur <input data-publish-field="host" value="${escapeHtml(selectedProfile.host)}"></label>
            <label>Port <input data-publish-field="port" type="number" value="${selectedProfile.port}"></label>
          </div>
          <div class="row two">
            <label>Utilisateur <input data-publish-field="username" value="${escapeHtml(selectedProfile.username)}"></label>
            <label>Répertoire distant <input data-publish-field="remotePath" value="${escapeHtml(selectedProfile.remotePath)}"></label>
          </div>
          <div class="row two">
            <label>URL publique <input data-publish-field="publicUrl" value="${escapeHtml(selectedProfile.publicUrl)}"></label>
            <label>Timeout (sec) <input data-publish-field="timeout" type="number" value="${selectedProfile.timeout}"></label>
          </div>
          ${selectedProfile.protocol === "sftp"
            ? `
              <div class="row two">
                <label>Empreinte hôte SHA-256 <input data-publish-field="sshFingerprint" value="${escapeHtml(selectedProfile.sshFingerprint || "")}" placeholder="SHA256:…"></label>
                <label>Chemin clé SSH <input data-publish-field="sshKeyPath" value="${escapeHtml(selectedProfile.sshKeyPath || "")}"></label>
              </div>
            `
            : `
              <div class="row">
                <label>Mode FTP
                  <select data-publish-field="passiveMode">
                    <option value="passive"${selectedProfile.passiveMode === "passive" ? " selected" : ""}>Passif</option>
                    <option value="active"${selectedProfile.passiveMode === "active" ? " selected" : ""}>Actif</option>
                  </select>
                </label>
              </div>
            `}
          <p class="status warn">Les secrets (mot de passe, passphrase) ne sont pas stockés ici.</p>
          ${renderSaveStatus()}
          <div class="actions">
            <button type="button" class="primary" data-action="publish-save"${state.publishBusy ? " disabled" : ""}>${state.publishBusy ? "Enregistrement…" : "Enregistrer"}</button>
            <button type="button" data-action="publish-new-from-current"${state.publishBusy ? " disabled" : ""}>Dupliquer</button>
          </div>
        </div>
      </div>
      <div class="panel">
        <h2>Publication programmée distante</h2>
        <div class="row two">
          <label>Profil automatique
            <select data-automatic-publish-field="profileId">
              ${profiles.map((profile) => `<option value="${escapeHtml(profile.id)}"${profile.id === automaticProfileId ? " selected" : ""}>${escapeHtml(profile.name)}</option>`).join("")}
            </select>
          </label>
          <label class="checkbox-row checkbox-row--inline">
            <input type="checkbox" data-automatic-publish-field="enabled"${state.automaticPublishSettings.enabled ? " checked" : ""}>
            <span>Publier automatiquement les articles programmés</span>
          </label>
        </div>
        <div class="row two">
          <label>Mot de passe automatique
            <input type="password" name="automatic-publish-password" data-automatic-publish-field="password" autocomplete="new-password" spellcheck="false" placeholder="${state.automaticPublishSettings.secretConfigured ? "Secret déjà configuré — laisser vide pour le conserver" : "Obligatoire avant activation"}">
          </label>
          <label>Passphrase de clé SSH (optionnelle)
            <input type="password" name="automatic-publish-passphrase" data-automatic-publish-field="passphrase" autocomplete="new-password" spellcheck="false">
          </label>
        </div>
        <p class="status ${state.automaticPublishSettings.secretConfigured ? "ok" : "warn"}">
          ${state.automaticPublishSettings.secretConfigured
            ? `Secret chiffré configuré${state.automaticPublishSettings.secretUpdatedAt ? ` le ${escapeHtml(new Date(state.automaticPublishSettings.secretUpdatedAt).toLocaleString("fr-FR"))}` : ""}.`
            : "Aucun secret automatique n’est enregistré sur le VPS."}
        </p>
        <p class="status ${state.automaticPublishSettings.workerActive ? "ok" : "err"}">
          ${state.automaticPublishSettings.workerActive
            ? `Worker actif${state.automaticPublishSettings.workerLastSeenAt ? ` — dernier signal le ${escapeHtml(new Date(state.automaticPublishSettings.workerLastSeenAt).toLocaleString("fr-FR"))}` : ""}.`
            : "Worker inactif ou non encore démarré : aucune publication programmée ne pourra partir."}
        </p>
        <div class="actions">
          <button type="button" class="primary" data-action="automatic-publish-save"${state.publishBusy ? " disabled" : ""}>Enregistrer l’automatisation</button>
          <button type="button" data-action="automatic-publish-delete-secret"${!state.automaticPublishSettings.secretConfigured || state.publishBusy ? " disabled" : ""}>Supprimer le secret</button>
        </div>
      </div>
      <div class="panel">
        <h2>Dernières tâches programmées</h2>
        ${jobs.length ? `
          <div class="table-wrap">
            <table>
              <thead><tr><th>Échéance</th><th>État</th><th>Tentatives</th><th>Message</th><th></th></tr></thead>
              <tbody>
                ${jobs.map((job) => `
                  <tr>
                    <td>${escapeHtml(new Date(job.scheduledFor).toLocaleString("fr-FR"))}</td>
                    <td>${escapeHtml({ queued: "En attente", running: "En cours", succeeded: "Réussie", failed: "Échec", cancelled: "Annulée" }[job.status])}</td>
                    <td>${job.attempts}/${job.maxAttempts}</td>
                    <td>${escapeHtml(job.error || "—")}</td>
                    <td>${job.status === "failed" ? `<button type="button" data-action="automatic-publish-retry" data-job-id="${escapeHtml(job.id)}">Relancer</button>` : ""}</td>
                  </tr>
                `).join("")}
              </tbody>
            </table>
          </div>
        ` : '<p class="status">Aucune tâche programmée enregistrée.</p>'}
      </div>
    </div>
  `;
}

function renderHtaccessSettings(): string {
  return `
    <div class="editor">
      <div class="panel publish-htaccess">
        <h2>Paramètres &gt; .htaccess</h2>
        <label class="checkbox-row checkbox-row--inline">
          <input type="checkbox" data-publish-field="htaccessEnabled"${state.publishConfig.htaccessEnabled ? " checked" : ""}>
          <span>Activer le fichier .htaccess</span>
        </label>
        <label class="publish-htaccess__editor">
          <span>Contenu du .htaccess</span>
          <textarea data-publish-field="htaccessContent" spellcheck="false" placeholder="# Créez ou collez ici vos règles Apache">${escapeHtml(state.publishConfig.htaccessContent || "")}</textarea>
        </label>
        <p class="status">Le fichier est toujours créé à la racine du site généré et publié. Lorsqu’il est désactivé, ses directives sont préfixées par # afin de conserver le contenu sans appliquer les règles Apache.</p>
        ${renderSaveStatus()}
        <div class="actions">
          <button type="button" class="primary" data-action="htaccess-save">Enregistrer</button>
        </div>
      </div>
    </div>
  `;
}

function renderStatisticsSettings(): string {
  const connectionStatus = state.supabaseConnectionStatus
    ? `<p class="status ${state.supabaseConnectionStatus.ok ? "ok" : "err"}">${escapeHtml(state.supabaseConnectionStatus.message)}</p>`
    : `<p class="status">Statut de connexion : non testé.</p>`;

  return `
    <div class="editor">
      <div class="panel">
        <h2>Paramètres &gt; Statistiques</h2>
        <div class="row two">
          <label>
            Fournisseur
            <select data-site-field="analyticsProvider">
              <option value="supabase"${(state.config?.analyticsProvider || "supabase") === "supabase" ? " selected" : ""}>Supabase</option>
              <option value="none"${state.config?.analyticsProvider === "none" ? " selected" : ""}>Aucun</option>
            </select>
          </label>
          <label class="checkbox-row checkbox-row--inline">
            <input type="checkbox" data-site-field="analyticsEnabled"${state.config?.analyticsEnabled !== false ? " checked" : ""}>
            <span>Activer la collecte des vues</span>
          </label>
        </div>
        <div class="row two">
          <label>
            ${renderHelpLabel("URL Supabase", "URL du projet Supabase, par exemple https://mon-projet.supabase.co", "Aide Supabase URL")}
            <input data-site-field="supabaseUrl" value="${escapeHtml(state.config?.supabaseUrl || "")}" placeholder="https://mon-projet.supabase.co">
          </label>
          <label>
            ${renderHelpLabel("Clé publique Supabase", "Clé publishable/anon utilisée par le site public pour appeler la fonction Edge de tracking.", "Aide Supabase key")}
            <input data-site-field="supabaseAnonKey" value="${escapeHtml(state.config?.supabaseAnonKey || "")}" placeholder="sb_publishable_...">
          </label>
        </div>
        <div class="row">
          <label class="checkbox-row checkbox-row--inline">
            <input type="checkbox" data-site-field="showViewCounter"${state.config?.showViewCounter !== false ? " checked" : ""}>
            <span>Afficher le compteur de vues sur les articles</span>
          </label>
        </div>
        <p class="status">Le dashboard Analytics passe par la fonction Edge sécurisée « analytics-stats ». Aucune clé serveur n’est stockée dans Studio.</p>
        ${connectionStatus}
        ${renderSaveStatus()}
        <div class="actions">
          <button type="button" class="primary" data-action="site-settings-save">Enregistrer</button>
          <button type="button" data-action="test-supabase-connection">Tester la connexion</button>
          <button type="button" data-action="open-supabase">Ouvrir Supabase</button>
        </div>
      </div>
    </div>
  `;
}

function renderOpenAISettings(): string {
  const status = state.openAIStatus
    ? `<p class="status ${state.openAIStatus.ok ? "ok" : "err"}">${escapeHtml(state.openAIStatus.message)}</p>`
    : `<p class="status ${state.openAISettings.configured ? "ok" : "warn"}">${state.openAISettings.configured ? "Clé OpenAI configurée sur cet appareil." : "Aucune clé OpenAI configurée."}</p>`;
  return `
    <div class="editor">
      <div class="panel">
        <h2>Paramètres &gt; OpenAI</h2>
        <div class="row two">
          <label>
            Clé API OpenAI
            <input type="password" data-openai-field="apiKey" value="" autocomplete="off" placeholder="${state.openAISettings.configured ? "Clé enregistrée — laisser vide pour la conserver" : "sk-proj-…"}">
          </label>
          <label>
            Modèle
            <input data-openai-field="model" value="${escapeHtml(state.openAISettings.model)}" placeholder="gpt-4.1-mini">
          </label>
        </div>
        <p class="status">La clé est chiffrée localement par Electron et n’est jamais envoyée à Supabase. Les requêtes sont faites directement depuis ce Mac vers OpenAI.</p>
        ${status}
        <div class="actions">
          <button type="button" class="primary" data-action="openai-settings-save">Enregistrer</button>
          <button type="button" data-action="openai-settings-clear"${state.openAISettings.configured ? "" : " disabled"}>Supprimer la clé</button>
        </div>
      </div>
    </div>
  `;
}

function renderGoogleSettings(): string {
  return `
    <div class="editor">
      <div class="panel">
        <h2>Paramètres &gt; Google</h2>
        <div class="row">
          <label>
            ${renderHelpLabel(
              "Description SEO du site",
              `Description utilisée sur la page d’accueil et comme complément pour les pages dont le résumé est trop court. Saisissez entre ${SEO_DESCRIPTION_MIN_LENGTH} et ${SEO_DESCRIPTION_MAX_LENGTH} caractères.`,
              "Aide description SEO",
              {
                fieldName: "seoDescription",
                value: state.config?.seoDescription || DEFAULT_SITE_SEO_DESCRIPTION,
                minLength: SEO_DESCRIPTION_MIN_LENGTH,
                maxLength: SEO_DESCRIPTION_MAX_LENGTH
              }
            )}
            <textarea data-site-field="seoDescription" minlength="${SEO_DESCRIPTION_MIN_LENGTH}" maxlength="${SEO_DESCRIPTION_MAX_LENGTH}">${escapeHtml(state.config?.seoDescription || DEFAULT_SITE_SEO_DESCRIPTION)}</textarea>
          </label>
        </div>
        <div class="row two">
          <label class="checkbox-row checkbox-row--inline">
            <input type="checkbox" data-site-field="googleAnalyticsEnabled"${state.config?.googleAnalyticsEnabled ? " checked" : ""}>
            <span>Activer Google Analytics</span>
          </label>
          <label>
            ${renderHelpLabel("Measurement ID", "Identifiant Google Analytics 4, par exemple G-XXXXXXXXXX. Il sera injecté dans les pages publiques du site.", "Aide Google Analytics")}
            <input data-site-field="googleAnalyticsId" value="${escapeHtml(state.config?.googleAnalyticsId || "")}" placeholder="G-XXXXXXXXXX">
          </label>
        </div>
        <p class="status">La balise Google Analytics sera ajoutée dans le &lt;head&gt; du site généré après un nouveau build.</p>
        ${renderSaveStatus()}
        <div class="actions">
          <button type="button" class="primary" data-action="site-settings-save">Enregistrer</button>
        </div>
      </div>
    </div>
  `;
}

function renderSocialSettings(): string {
  const selectedNetworks = new Set(state.config?.socialNetworks || DEFAULT_SOCIAL_NETWORKS);
  const selectedCount = SOCIAL_NETWORKS.filter((network) => selectedNetworks.has(network.id)).length;

  return `
    <div class="editor">
      <div class="panel">
        <h2>Paramètres &gt; Social</h2>
        <div class="row">
          <label class="checkbox-row checkbox-row--inline">
            <input type="checkbox" data-site-field="socialSharingEnabled"${state.config?.socialSharingEnabled !== false ? " checked" : ""}>
            <span>Afficher les boutons de partage en bas des articles</span>
          </label>
        </div>
        <p class="status">Choisissez les réseaux à afficher. Les boutons seront ajoutés sous chaque article avec une ligne de séparation.</p>
        <div class="actions">
          <button type="button" data-action="social-select-default">Principaux</button>
          <button type="button" data-action="social-select-all">Tout cocher</button>
          <button type="button" data-action="social-clear-all">Tout décocher</button>
        </div>
        <p class="status">${selectedCount} réseau(x) sélectionné(s) sur ${SOCIAL_NETWORKS.length}.</p>
        <div class="checkbox-grid social-grid">
          ${SOCIAL_NETWORKS.map((network) => `
            <label class="checkbox-row">
              <input
                type="checkbox"
                data-social-network="${escapeHtml(network.id)}"
                ${selectedNetworks.has(network.id) ? "checked" : ""}
              >
              <span>${escapeHtml(network.label)}</span>
            </label>
          `).join("")}
        </div>
        ${renderSaveStatus()}
        <div class="actions">
          <button type="button" class="primary" data-action="site-settings-save">Enregistrer</button>
        </div>
      </div>
    </div>
  `;
}

function renderThemeSettings(): string {
  const selectedTheme = state.config?.template || "v6";
  const disabledThemes = new Set(state.config?.disabledThemes || []);
  const availableThemes = SITE_THEMES.filter((theme) => theme.id === "v6" || !disabledThemes.has(theme.id));
  return `
    <div class="editor">
      <div class="panel">
        <h2>Paramètres &gt; Thème du site</h2>
        <p class="status">Le thème modifie la présentation. Les balises HTML, le SEO, Open Graph, JSON-LD, le sitemap et les contrôles de publication restent gérés par LBStudio.</p>
        <div class="theme-options">
          ${availableThemes.map((theme) => `
            <div class="theme-option${selectedTheme === theme.id ? " theme-option--selected" : ""}">
              <label class="theme-option__selection">
                <input type="radio" name="site-theme" data-site-field="template" value="${escapeHtml(theme.id)}"${selectedTheme === theme.id ? " checked" : ""}>
                <span>
                  <strong>${escapeHtml(theme.name)}</strong>
                  <small>${escapeHtml(theme.description)}</small>
                </span>
              </label>
              ${theme.id === "v6" ? '<span class="theme-option__protected">Intégré</span>' : `<button type="button" class="danger theme-option__delete" data-action="theme-delete" data-theme-id="${escapeHtml(theme.id)}">Supprimer</button>`}
            </div>
          `).join("")}
        </div>
        ${renderSaveStatus()}
        <div class="actions">
          <button type="button" class="primary" data-action="site-settings-save">Enregistrer le thème</button>
        </div>
      </div>
    </div>
  `;
}

function renderPageEditor(page: PageRecord): string {
  return `
    <div class="editor">
      <div class="row two">
        <label>Titre <input data-page-field="title" value="${escapeHtml(page.frontMatter.title)}"></label>
        <label>${renderHelpLabel("Slug", "Identifiant utilise dans l'URL de la page. Utilise de preference des mots courts, sans accents ni espaces. Exemple : mentions-legales", "Aide slug")}<input data-page-field="slug" value="${escapeHtml(page.frontMatter.slug)}"></label>
      </div>
      <div class="row two">
        <label>Ordre <input data-page-field="order" type="number" value="${page.frontMatter.order ?? ""}"></label>
        ${renderSeoField("SEO title", "seo_title", page.frontMatter.seo_title || "", true)}
      </div>
      ${renderSeoField("SEO description", "seo_description", page.frontMatter.seo_description || "", true)}
      ${renderEditorModeSwitch()}
      <div class="row">
        <div class="editor-label">Contenu</div>
        <div class="wysiwyg-shell">
          <div data-body-editor="page"></div>
        </div>
        <div class="editor-help">Edition visuelle WYSIWYG avec stockage Markdown.</div>
      </div>
      ${renderSaveStatus()}
      <div class="actions">
        <button type="button" class="primary" data-action="save-page">Enregistrer la page</button>
        <button type="button" data-action="preview">Aperçu du site</button>
        <button type="button" data-action="markdown-preview">Aperçu Markdown</button>
        <button type="button" data-action="delete-page">Supprimer la page</button>
      </div>
    </div>
  `;
}

function renderCategorySettings(): string {
  return `
    <div class="editor category-settings">
      <h2>Gestion des catégories</h2>
      <p class="meta">Les catégories actives apparaissent dans la navigation du site et dans l’éditeur des articles.</p>
      ${renderSaveStatus()}
      <div class="category-editor-list">
        ${[...state.categories]
          .sort((left, right) => left.order - right.order)
          .map((category) => `
            <div class="category-editor-row" data-category-editor data-category-id="${escapeHtml(category.id)}">
              <label>Nom <input data-category-field="name" value="${escapeHtml(category.name)}"></label>
              <label>Slug <input data-category-field="slug" value="${escapeHtml(category.slug)}"></label>
              <label>Ordre <input type="number" data-category-field="order" value="${category.order}"></label>
              <label class="checkbox-row category-enabled"><input type="checkbox" data-category-field="enabled"${category.enabled ? " checked" : ""}> <span>Active</span></label>
              <div class="actions category-actions">
                <button type="button" class="primary" data-action="save-category">Enregistrer</button>
                <button type="button" data-action="delete-category">Archiver</button>
              </div>
            </div>
          `).join("") || '<p class="meta">Aucune catégorie.</p>'}
      </div>
      <div class="category-create" data-category-editor>
        <h3>Ajouter une catégorie</h3>
        <div class="row two">
          <label>Nom <input data-category-field="name" placeholder="Nouvelle catégorie"></label>
          <label>Slug <input data-category-field="slug" placeholder="nouvelle-categorie"></label>
        </div>
        <div class="row two">
          <label>Ordre <input type="number" data-category-field="order" value="${state.categories.length + 1}"></label>
          <label class="checkbox-row checkbox-row--inline"><input type="checkbox" data-category-field="enabled" checked> <span>Active</span></label>
        </div>
        <div class="actions"><button type="button" class="primary" data-action="save-category">Ajouter</button></div>
      </div>
    </div>
  `;
}

function renderPageSettings(): string {
  return `
    <div class="editor page-settings">
      <div class="management-heading">
        <div>
          <h2>Gestion des pages</h2>
          <p class="meta">L’ordre détermine la position des liens dans le pied de page du site.</p>
        </div>
        <button type="button" class="primary" data-action="new-page">Nouvelle page</button>
      </div>
      ${renderSaveStatus()}
      <div class="page-editor-list">
        ${sortPages(state.pages).map((page) => `
          <div class="page-editor-row" data-page-editor data-page-editor-id="${escapeHtml(page.id)}">
            <label>Titre <input data-page-summary-field="title" value="${escapeHtml(page.frontMatter.title)}"></label>
            <label>Slug <input data-page-summary-field="slug" value="${escapeHtml(page.frontMatter.slug)}"></label>
            <label>Ordre <input type="number" data-page-summary-field="order" value="${page.frontMatter.order ?? ""}"></label>
            <div class="actions page-actions">
              <button type="button" class="primary" data-action="save-page-summary">Enregistrer</button>
              <button type="button" data-action="edit-page">Modifier</button>
              <button type="button" data-action="delete-page-summary">Supprimer</button>
            </div>
          </div>
        `).join("") || '<p class="meta">Aucune page.</p>'}
      </div>
    </div>
  `;
}

function renderMediaLibrary(): string {
  const activeWorkspace = state.auth?.workspaces.find((workspace) => workspace.id === state.auth?.workspaceId);
  const canUpload = canUploadMedia();
  const canDelete = state.auth?.contentMode === "local" || activeWorkspace?.role === "admin" || activeWorkspace?.role === "editor";
  const status = state.mediaStatus
    ? `<p class="status ${state.mediaStatus.ok ? "ok" : "err"}">${escapeHtml(state.mediaStatus.message)}</p>`
    : "";
  return `
    <div class="editor media-library">
      <div class="management-heading">
        <div>
          <h2>Médiathèque</h2>
          <p class="meta">Les médias encore référencés par un article ou une page ne peuvent pas être supprimés.</p>
        </div>
        <div class="actions">
          ${canUpload ? `<label class="primary media-upload-button">
            Ajouter des médias
            <input type="file" multiple data-media-upload>
          </label>` : ""}
          <button type="button" data-action="scan-media-orphans"${state.mediaLoading ? " disabled" : ""}>Vérifier l’intégrité</button>
        </div>
      </div>
      ${status}
      <div class="media-grid">
        ${state.mediaAssets.map((asset) => `
          <article class="media-card">
            ${asset.previewUrl ? `
              <div class="media-card__preview">
                <img src="${escapeHtml(asset.previewUrl)}" alt="Aperçu de ${escapeHtml(asset.fileName)}" loading="lazy">
              </div>
            ` : `<div class="media-card__preview media-card__preview--empty">Aucun aperçu disponible</div>`}
            <div class="media-card__name">${escapeHtml(asset.fileName)}</div>
            <code>${escapeHtml(asset.publicPath)}</code>
            <div class="meta">${formatBytes(asset.sizeBytes)} · ${new Date(asset.createdAt).toLocaleDateString("fr-FR")}</div>
            ${asset.width && asset.height ? `<div class="meta">${asset.width} × ${asset.height} px${asset.variantPublicPaths?.length ? ` · ${asset.variantPublicPaths.length} variante(s)` : ""}</div>` : ""}
            <div class="actions">
              <button type="button" data-action="copy-media-path" data-media-path="${escapeHtml(asset.publicPath)}">Copier le chemin</button>
              ${canDelete
                ? `<button type="button" data-action="delete-media" data-media-id="${escapeHtml(asset.id)}">Supprimer</button>`
                : ""}
            </div>
          </article>
        `).join("") || '<p class="meta">Aucun média.</p>'}
      </div>
      ${state.mediaOrphans.length > 0 ? `
        <div class="media-orphans">
          <h3>Éléments orphelins</h3>
          <p class="meta">Ces éléments existent uniquement dans Storage ou uniquement dans les métadonnées SQL.</p>
          ${state.mediaOrphans.map((orphan, index) => `
            <div class="media-orphan-row">
              <div>
                <strong>${orphan.kind === "missing_storage" ? "Fichier Storage manquant" : "Métadonnée SQL manquante"}</strong>
                <code>${escapeHtml(orphan.storagePath)}</code>
              </div>
              ${canDelete ? `<button type="button" data-action="delete-media-orphan" data-media-orphan-index="${index}">Nettoyer</button>` : ""}
            </div>
          `).join("")}
        </div>
      ` : ""}
    </div>
  `;
}

function canUploadMedia(): boolean {
  const activeWorkspace = state.auth?.workspaces.find((workspace) => workspace.id === state.auth?.workspaceId);
  return state.auth?.contentMode === "local"
    || activeWorkspace?.role === "admin"
    || activeWorkspace?.role === "editor"
    || activeWorkspace?.role === "author";
}

function renderArticleMediaField(article: ArticleRecord): string {
  const selectedPath = article.frontMatter.image || "";
  const selectedAsset = state.mediaAssets.find((asset) => asset.publicPath === selectedPath);
  const missingSelection = selectedPath && !selectedAsset;
  return `
    <div class="article-media-field">
      <label>
        Image principale
        <select data-field="image">
          <option value="">Image générique par défaut</option>
          ${missingSelection ? `<option value="${escapeHtml(selectedPath)}" selected>Média introuvable — ${escapeHtml(selectedPath)}</option>` : ""}
          ${state.mediaAssets.filter((asset) => asset.previewUrl).map((asset) => `
            <option value="${escapeHtml(asset.publicPath)}"${asset.publicPath === selectedPath ? " selected" : ""}>
              ${escapeHtml(asset.fileName)}
            </option>
          `).join("")}
        </select>
      </label>
      ${selectedAsset?.previewUrl ? `
        <div class="article-media-preview">
          <img src="${escapeHtml(selectedAsset.previewUrl)}" alt="Aperçu de ${escapeHtml(selectedAsset.fileName)}">
        </div>
      ` : ""}
      ${canUploadMedia() ? `
        <label class="primary media-upload-button article-media-upload">
          ${state.articleMediaLoading ? "Import en cours…" : "Importer une nouvelle image"}
          <input type="file" accept="image/*" data-article-media-upload${state.articleMediaLoading ? " disabled" : ""}>
        </label>
      ` : ""}
      ${state.articleMediaStatus ? `<p class="status ${state.articleMediaStatus.ok ? "ok" : "err"}">${escapeHtml(state.articleMediaStatus.message)}</p>` : ""}
    </div>
  `;
}

function renderAnalyticsDashboard(): string {
  const filteredEvents = filterAnalyticsEvents(state.analyticsEvents, state.analyticsRange, state.analyticsQuery);
  const data = summarizeAnalyticsEvents(filteredEvents);

  return `
    <div class="analytics-dashboard">
      <div class="analytics-toolbar">
        <label>
          Période
          <select data-analytics-range>
            <option value="7d"${state.analyticsRange === "7d" ? " selected" : ""}>7 jours</option>
            <option value="30d"${state.analyticsRange === "30d" ? " selected" : ""}>30 jours</option>
            <option value="all"${state.analyticsRange === "all" ? " selected" : ""}>Tout</option>
          </select>
        </label>
        <label class="analytics-search">
          Recherche
          <input type="search" placeholder="Slug, titre, referrer…" data-analytics-search value="${escapeHtml(state.analyticsQuery)}">
        </label>
        <div class="actions analytics-actions">
          <button type="button" data-action="refresh-analytics">Actualiser</button>
          <button type="button" data-action="export-analytics-csv">Exporter CSV</button>
        </div>
      </div>

      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Total Vues</div>
          <div class="stat-value">${data.total_views || 0}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Visiteurs Uniques</div>
          <div class="stat-value">${data.unique_visitors || 0}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Articles</div>
          <div class="stat-value">${data.total_articles || 0}</div>
        </div>
      </div>

      <div class="analytics-container">
        <div class="chart-section">
          <h3>📈 Articles Populaires</h3>
          <div id="popular-articles">
            ${data.articles.length === 0
              ? `<div class="popular-item"><span class="title">Aucune vue pour le moment</span><span class="count">0 vues</span></div>`
              : data.articles
              .slice(0, 10)
              .map(
                (a: any) => `
              <div class="popular-item">
                <span class="title">${escapeHtml(a.title || a.slug)}</span>
                <span class="count">${a.views} vues</span>
              </div>
            `
              )
              .join("")}
          </div>
        </div>

        <div class="chart-section">
          <h3>📊 Vues par jour</h3>
          <canvas id="views-timeline" style="max-height: 200px;"></canvas>
        </div>
      </div>

      <div class="analytics-table">
        <h3>Statistiques par article</h3>
        <table>
          <thead>
            <tr>
              <th>Article</th>
              <th>Vues</th>
              <th>Visiteurs uniques</th>
              <th>Dernière vue</th>
            </tr>
          </thead>
          <tbody>
            ${data.articles
              .map(
                (a: any) => `
              <tr>
                <td>${escapeHtml(a.title || a.slug)}</td>
                <td>${a.views}</td>
                <td>${a.unique_visitors}</td>
                <td>${a.last_view ? new Date(a.last_view).toLocaleDateString("fr-FR") : "--"}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

function renderTeamSettings(section: "sites" | "users"): string {
  const auth = state.auth;
  if (!auth?.configured) {
    return `
      <div class="editor">
        <h2>Équipe</h2>
        <p class="status warn">Renseignez d’abord l’URL et la clé publique Supabase dans Paramètres &gt; Statistiques.</p>
      </div>
    `;
  }

  const status = state.teamStatus
    ? `<p class="status ${state.teamStatus.ok ? "ok" : "err"}">${escapeHtml(state.teamStatus.message)}</p>`
    : "";
  if (!auth.authenticated) {
    return `
      <div class="editor team-settings">
        <h2>Connexion à la rédaction</h2>
        <div class="row two">
          <label>Email <input type="email" data-team-field="email" autocomplete="${state.suppressLoginAutofill ? "off" : "username"}"></label>
          <label>Mot de passe <input type="password" data-team-field="password" autocomplete="${state.suppressLoginAutofill ? "new-password" : "current-password"}"></label>
        </div>
        ${status}
        <div class="actions">
          <button type="button" class="primary" data-action="team-sign-in">Se connecter</button>
          <button type="button" data-action="open-password-reset">Mot de passe oublié ?</button>
        </div>
      </div>
    `;
  }

  const activeWorkspace = auth.workspaces.find((workspace) => workspace.id === auth.workspaceId);
  const isAdmin = activeWorkspace?.role === "admin";
  const roleOptions = (selected: TeamRole) => (["admin", "editor", "author", "viewer"] as TeamRole[])
    .map((role) => `<option value="${role}"${role === selected ? " selected" : ""}>${teamRoleLabel(role)}</option>`)
    .join("");
  const accountSecurity = `
    <div class="panel team-administration">
      <h3>Changer mon mot de passe</h3>
      <p class="status">Votre mot de passe reste géré exclusivement par Supabase et n’est jamais stocké par LBStudio.</p>
      <div class="row">
        <label>Mot de passe actuel <input type="password" data-team-field="current-password" autocomplete="current-password"></label>
      </div>
      <div class="row two">
        <label>Nouveau mot de passe <input type="password" data-team-field="new-password" autocomplete="new-password" minlength="${PASSWORD_MIN_LENGTH}"></label>
        <label>Confirmer le nouveau mot de passe <input type="password" data-team-field="new-password-confirmation" autocomplete="new-password" minlength="${PASSWORD_MIN_LENGTH}"></label>
      </div>
      <div class="actions">
        <button type="button" class="primary" data-action="change-own-password">Changer mon mot de passe</button>
      </div>
    </div>
  `;
  const administration = isAdmin
    ? `
      <div class="team-members">
        <div class="team-section-heading">
          <h3>Membres du site</h3>
          <button type="button" data-action="team-refresh-members">Actualiser</button>
        </div>
        ${state.teamLoading
          ? '<p class="status">Chargement…</p>'
          : `
            <table class="team-members-table">
              <thead><tr><th>Utilisateur</th><th>Email</th><th>Rôle</th><th>Actions</th></tr></thead>
              <tbody>
                ${state.teamMembers.map((member) => `
                  <tr>
                    <td>${escapeHtml(member.displayName || member.email)}${member.currentUser ? " (vous)" : ""}</td>
                    <td>${escapeHtml(member.email)}</td>
                    <td>
                      <select data-team-role data-user-id="${escapeHtml(member.userId)}"${member.currentUser ? " disabled" : ""}>
                        ${roleOptions(member.role)}
                      </select>
                    </td>
                    <td>
                      <button type="button" data-action="team-send-password-reset" data-user-id="${escapeHtml(member.userId)}">Envoyer un lien</button>
                      ${member.currentUser ? "" : `<button type="button" data-action="team-remove-member" data-user-id="${escapeHtml(member.userId)}">Retirer</button>`}
                    </td>
                  </tr>
                `).join("") || '<tr><td colspan="4">Aucun membre chargé.</td></tr>'}
              </tbody>
            </table>
          `}
      </div>
      <div class="panel team-administration">
        <h3>Inviter un membre</h3>
        <div class="row two">
          <label>Nom affiché <input data-team-field="member-name" placeholder="Prénom Nom"></label>
          <label>Email <input type="email" data-team-field="member-email" autocomplete="off"></label>
        </div>
        <div class="row">
          <label>Rôle
            <select data-team-field="member-role">${roleOptions("author")}</select>
          </label>
        </div>
        <p class="status">Le membre recevra un email Supabase et choisira lui-même son mot de passe.</p>
        <div class="actions">
          <button type="button" class="primary" data-action="team-invite-member">Envoyer l’invitation</button>
        </div>
      </div>
    `
    : activeWorkspace
      ? `<p class="status">La gestion des membres est réservée aux administrateurs du site.</p>`
      : "";
  const showSiteCreation = state.siteCreationOpen || auth.workspaces.length === 0;
  const siteManagement = `
      <div class="panel team-sites">
        <div class="team-section-heading">
          <div>
            <p class="team-eyebrow">Sites</p>
            <h3>Gestion des sites</h3>
          </div>
        </div>
        ${auth.workspaces.length > 1
          ? `
            <div class="team-site-list">
              ${auth.workspaces.map((workspace) => `
                <button type="button" class="team-site-choice${workspace.id === auth.workspaceId ? " active" : ""}" data-action="team-select-workspace" data-workspace-id="${escapeHtml(workspace.id)}">
                  <span>
                    <strong>${escapeHtml(workspaceDisplayName(workspace))}</strong>
                    <small>${escapeHtml(workspace.slug || "Identifiant non défini")}</small>
                  </span>
                  <span class="team-role">${escapeHtml(teamRoleLabel(workspace.role))}${workspace.id === auth.workspaceId ? " · Actif" : ""}</span>
                </button>
              `).join("")}
            </div>
          `
          : ""}
        ${isAdmin && activeWorkspace
          ? `
            <div class="team-site-edit">
              <h4>Informations du site actif</h4>
              <div class="row two">
                <label>Nom du site <input data-team-field="site-name" value="${escapeHtml(activeWorkspace.name)}" placeholder="Les Boules"></label>
                <label>Identifiant technique <input data-team-field="site-slug" value="${escapeHtml(activeWorkspace.slug)}" placeholder="les-boules"></label>
              </div>
              <div class="actions">
                <button type="button" class="primary" data-action="team-update-workspace">Enregistrer</button>
              </div>
            </div>
          `
          : ""}
        ${isAdmin || auth.workspaces.length === 0
          ? `
            <div class="team-site-create">
              ${showSiteCreation
                ? `
                  <h4>Créer un nouveau site</h4>
                  <div class="row two">
                    <label>Nom du site <input data-team-field="workspace-name" placeholder="Nouveau site"></label>
                    <label>Identifiant technique <input data-team-field="workspace-slug" placeholder="nouveau-site"></label>
                  </div>
                  <div class="actions">
                    <button type="button" class="primary" data-action="team-create-workspace">Créer le site</button>
                    ${auth.workspaces.length ? '<button type="button" data-action="team-site-creation-toggle">Annuler</button>' : ""}
                  </div>
                `
                : '<button type="button" data-action="team-site-creation-toggle">+ Ajouter un site</button>'}
            </div>
          `
          : ""}
      </div>
    `;

  return `
    <div class="editor team-settings">
      <div class="team-heading">
        <div>
          <p class="team-eyebrow">${section === "sites" ? "Configuration" : "Équipe et accès"}</p>
          <h2>${section === "sites" ? "Gestion du site" : "Utilisateurs"}</h2>
          <p class="status">${activeWorkspace ? escapeHtml(workspaceDisplayName(activeWorkspace)) : "Aucun site"} · Connecté : ${escapeHtml(auth.email || "")}</p>
        </div>
      </div>
      ${activeWorkspace
        ? `
          <div class="panel team-active-site">
            <div>
              <p class="team-eyebrow">Site actif</p>
              <h3>${escapeHtml(workspaceDisplayName(activeWorkspace))}</h3>
              <p class="status">Identifiant : ${escapeHtml(activeWorkspace.slug || "non défini")} · Rôle : ${escapeHtml(teamRoleLabel(activeWorkspace.role))}</p>
            </div>
          </div>
        `
        : '<p class="status warn">Créez votre premier site pour commencer.</p>'}
      ${status}
      ${section === "users" ? `${accountSecurity}${administration}` : siteManagement}
    </div>
  `;
}

async function loadTeamMembers(): Promise<void> {
  const activeWorkspace = state.auth?.workspaces.find((workspace) => workspace.id === state.auth?.workspaceId);
  if (activeWorkspace?.role !== "admin") {
    state.teamMembers = [];
    return;
  }
  state.teamLoading = true;
  render();
  try {
    state.teamMembers = await window.lesBoulesStudio.listTeamMembers();
  } catch (error) {
    state.teamStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
  } finally {
    state.teamLoading = false;
    render();
  }
}

function renderAuthenticationGate(): void {
  if (!state.auth?.configured) {
    app.innerHTML = `
      <main class="auth-screen">
        <section class="panel auth-panel">
          <h1>LES BOULES STUDIO</h1>
          <p>Configurez la connexion au projet Supabase pour démarrer.</p>
          <div class="editor">
            <label>URL Supabase <input type="url" data-cloud-field="url" placeholder="https://votre-projet.supabase.co"></label>
            <label>Clé publique Supabase <input type="password" data-cloud-field="anon-key" autocomplete="off"></label>
            ${state.teamStatus ? `<p class="status ${state.teamStatus.ok ? "ok" : "err"}">${escapeHtml(state.teamStatus.message)}</p>` : ""}
            <div class="actions"><button type="button" class="primary" data-action="cloud-configure">Configurer Supabase</button></div>
          </div>
        </section>
      </main>
    `;
    attachHandlers();
    return;
  }
  if (state.auth.passwordRecovery) {
    app.innerHTML = `
      <main class="auth-screen">
        <section class="panel auth-panel">
          <h1>Choisir un nouveau mot de passe</h1>
          <p>Le lien d’invitation ou de récupération a été validé. Choisissez un nouveau mot de passe d’au moins ${PASSWORD_MIN_LENGTH} caractères.</p>
          <div class="editor">
            <label>Nouveau mot de passe <input type="password" data-team-field="recovery-password" autocomplete="new-password" minlength="${PASSWORD_MIN_LENGTH}"></label>
            <label>Confirmer le mot de passe <input type="password" data-team-field="recovery-password-confirmation" autocomplete="new-password" minlength="${PASSWORD_MIN_LENGTH}"></label>
            ${state.teamStatus ? `<p class="status ${state.teamStatus.ok ? "ok" : "err"}">${escapeHtml(state.teamStatus.message)}</p>` : ""}
            <div class="actions">
              <button type="button" class="primary" data-action="complete-password-recovery">Enregistrer le nouveau mot de passe</button>
            </div>
          </div>
        </section>
      </main>
    `;
    attachHandlers();
    return;
  }
  app.innerHTML = `
    <main class="auth-screen">
      <section class="panel auth-panel">
        <h1>LES BOULES STUDIO</h1>
        <p>Connectez-vous pour accéder à l’espace de rédaction partagé.</p>
        <div class="editor">
          <label>Email <input type="email" data-team-field="email" autocomplete="${state.suppressLoginAutofill ? "off" : "username"}"></label>
          <label>Mot de passe <input type="password" data-team-field="password" autocomplete="${state.suppressLoginAutofill ? "new-password" : "current-password"}"></label>
          ${state.teamStatus ? `<p class="status ${state.teamStatus.ok ? "ok" : "err"}">${escapeHtml(state.teamStatus.message)}</p>` : ""}
          <div class="actions">
            <button type="button" class="primary" data-action="team-sign-in">Se connecter</button>
            <button type="button" data-action="open-password-reset">Mot de passe oublié ?</button>
          </div>
        </div>
      </section>
    </main>
    ${state.passwordResetDialogOpen ? renderPasswordResetDialog() : ""}
  `;
  attachHandlers();
}

function renderPasswordResetDialog(): string {
  return `
    <div class="app-notice-overlay">
      <div class="app-notice">
        <div class="push-dialog__header">
          <div>
            <h2>Réinitialiser le mot de passe</h2>
            <p class="status">Saisissez l’adresse email du compte à récupérer.</p>
          </div>
          <button type="button" class="push-dialog__close" data-action="close-password-reset" aria-label="Fermer">×</button>
        </div>
        <div class="editor">
          <label>
            Email
            <input
              type="email"
              name="password-reset-recipient"
              data-team-field="password-reset-email"
              autocomplete="off"
              autocapitalize="none"
              spellcheck="false"
              value=""
              autofocus
            >
          </label>
          ${state.passwordResetStatus
            ? `<p class="status ${state.passwordResetStatus.ok ? "ok" : "err"}">${escapeHtml(state.passwordResetStatus.message)}</p>`
            : ""}
          <div class="actions">
            <button type="button" class="primary" data-action="request-password-reset">Envoyer le lien</button>
            <button type="button" data-action="close-password-reset">Annuler</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function render(): void {
  destroyBodyEditors();
  if (!state.auth?.authenticated || state.auth.passwordRecovery) {
    renderAuthenticationGate();
    return;
  }
  const selected = state.articles.find((article) => article.id === state.selectedArticleId);
  const selectedPage = sortPages(state.pages).find((page) => page.id === state.selectedPageId);
  const isPublicationSettings = state.activeMenu === "publication-settings";
  const isHtaccessSettings = state.activeMenu === "htaccess-settings";
  const isStatisticsSettings = state.activeMenu === "statistics-settings";
  const isOpenAISettings = state.activeMenu === "openai-settings";
  const isGoogleSettings = state.activeMenu === "google-settings";
  const isSocialSettings = state.activeMenu === "social-settings";
  const isThemeSettings = state.activeMenu === "theme-settings";
  const isSiteSettings = state.activeMenu === "site-settings";
  const isUserSettings = state.activeMenu === "user-settings";
  const isCategorySettings = state.activeMenu === "category-settings";
  const isPageSettings = state.activeMenu === "page-settings";
  const isMediaLibrary = state.activeMenu === "media";
  const isSettingsMenu = isPublicationSettings || isHtaccessSettings || isStatisticsSettings || isOpenAISettings || isGoogleSettings || isSocialSettings || isThemeSettings || isSiteSettings || isUserSettings || isCategorySettings;
  const recentArticles = [...state.articles]
    .sort((a, b) => b.frontMatter.updated.localeCompare(a.frontMatter.updated))
    .slice(0, 5);
  const filteredArticles = state.articles.filter((article) => {
    const haystack = [
      article.frontMatter.title,
      article.frontMatter.slug,
      state.categories.find((category) => category.id === article.frontMatter.categoryId)?.name || article.frontMatter.categoryId,
      article.frontMatter.tags.join(" "),
      article.body
    ]
      .join(" ")
      .toLowerCase();

    const matchesSearch = !state.filter || haystack.includes(state.filter.toLowerCase());
    const matchesCategory = state.categoryFilter === "all" || article.frontMatter.categoryId === state.categoryFilter;
    const matchesStatus = state.statusFilter === "all" || article.frontMatter.status === state.statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });
  const visibleArticles = sortArticles(filteredArticles);

  const categoryOptions = state.categories
    .filter((category) => category.enabled)
    .map((category) => `<option value="${escapeHtml(category.id)}"${category.id === selected?.frontMatter.categoryId ? " selected" : ""}>${escapeHtml(category.name)}</option>`)
    .join("");

  const editorHtml = state.activeMenu === "dashboard"
    ? `
      <div class="dashboard-side">
        <div class="panel">
          <h2>Dernières modifications</h2>
          <ul>${recentArticles.map((article) => `<li>${escapeHtml(article.frontMatter.title)}</li>`).join("")}</ul>
        </div>
        <div class="panel">
          <h2>Pages</h2>
          <ul>${sortPages(state.pages).map((page) => `<li>${escapeHtml(page.frontMatter.title)}</li>`).join("")}</ul>
        </div>
      </div>
    `
    : state.activeMenu === "category-settings"
    ? renderCategorySettings()
    : state.activeMenu === "page-settings"
    ? renderPageSettings()
    : state.activeMenu === "publication-settings"
    ? renderPublishSettings()
    : state.activeMenu === "htaccess-settings"
    ? renderHtaccessSettings()
    : state.activeMenu === "statistics-settings"
    ? renderStatisticsSettings()
    : state.activeMenu === "openai-settings"
    ? renderOpenAISettings()
    : state.activeMenu === "google-settings"
    ? renderGoogleSettings()
    : state.activeMenu === "social-settings"
    ? renderSocialSettings()
    : state.activeMenu === "theme-settings"
    ? renderThemeSettings()
    : state.activeMenu === "site-settings"
    ? renderTeamSettings("sites")
    : state.activeMenu === "user-settings"
    ? renderTeamSettings("users")
    : state.activeMenu === "media"
    ? renderMediaLibrary()
    : state.activeMenu === "analytics"
    ? renderAnalyticsDashboard()
    : selected
    ? `
      <div class="editor">
        <div class="row two">
          <label>
            ${renderHelpLabel(
              "Titre",
              `Titre éditorial affiché dans l’article. Entre ${ARTICLE_TITLE_MIN_LENGTH} et ${ARTICLE_TITLE_MAX_LENGTH} caractères, idéalement ${ARTICLE_TITLE_IDEAL_LENGTH}.`,
              "Aide titre",
              {
                fieldName: "title",
                value: selected.frontMatter.title,
                minLength: ARTICLE_TITLE_MIN_LENGTH,
                idealLength: ARTICLE_TITLE_IDEAL_LENGTH,
                maxLength: ARTICLE_TITLE_MAX_LENGTH
              }
            )}
            <input data-field="title" maxlength="${ARTICLE_TITLE_MAX_LENGTH}" value="${escapeHtml(selected.frontMatter.title)}">
          </label>
          <label>${renderHelpLabel("Slug", "Identifiant utilise dans l'URL de l'article. Utilise de preference des mots courts, sans accents ni espaces. Exemple : presidentielle-2027", "Aide slug")}<input data-field="slug" value="${escapeHtml(selected.frontMatter.slug)}"></label>
        </div>
        <div class="row two">
          <label>Catégorie <select data-field="category">${categoryOptions}</select></label>
          <label>Statut
            <select data-field="status">
              ${["draft", "published", "scheduled", "archived"]
                .map((status) => `<option value="${status}"${selected.frontMatter.status === status ? " selected" : ""}>${status}</option>`)
                .join("")}
            </select>
          </label>
        </div>
        <div class="row two">
          <label>Date <input data-field="date" value="${escapeHtml(selected.frontMatter.date)}"></label>
          <label>Auteur <input data-field="author" value="${escapeHtml(selected.frontMatter.author)}"></label>
        </div>
        <div class="row two">
          ${renderArticleMediaField(selected)}
          <label>Texte alternatif <input data-field="image_alt" value="${escapeHtml(selected.frontMatter.image_alt || "")}"></label>
        </div>
        <div class="row two">
          <label>${renderHelpLabel("Tags", "Mots-cles separes par des virgules pour classer ou retrouver plus facilement l'article. Exemple : politique, france, election", "Aide tags")}<input data-field="tags" value="${escapeHtml(selected.frontMatter.tags.join(", "))}"></label>
          <label class="checkbox-row checkbox-row--inline"><input type="checkbox" data-field="satire"${selected.frontMatter.satire ? " checked" : ""}> <span>Article satirique</span></label>
        </div>
        <div class="row two">
          ${renderSeoField("SEO title", "seo_title", selected.frontMatter.seo_title || "")}
          ${renderSeoField("SEO description", "seo_description", selected.frontMatter.seo_description || "")}
        </div>
        <div class="row">
          <label>
            ${renderHelpLabel(
              "Langue de l’article",
              "Détermine la langue HTML, la locale Open Graph et la langue des données structurées de cette page.",
              "Aide langue de l’article"
            )}
            <select data-field="locale">
              ${EUROPEAN_LOCALES.map((locale) => `
                <option value="${locale.value}"${locale.value === (selected.frontMatter.locale || DEFAULT_ARTICLE_LOCALE) ? " selected" : ""}>
                  ${escapeHtml(locale.label)} (${locale.value})
                </option>
              `).join("")}
            </select>
          </label>
        </div>
        ${renderFeaturedArticlePanel(selected)}
        ${renderEditorModeSwitch()}
        <div class="row">
          <div class="editor-label-row">
            <div class="editor-label">Corps</div>
            ${renderArticleWordCount(selected.body)}
          </div>
          <div class="wysiwyg-shell">
            <div data-body-editor="article"></div>
          </div>
          <div class="editor-help">Edition visuelle WYSIWYG avec stockage Markdown.</div>
        </div>
        ${renderSaveStatus()}
        ${state.aiGenerationNote && state.aiGeneratedArticleId === selected.id ? `
          <div class="ai-generation-result">
            <strong>Note au rédacteur</strong>
            <p>${escapeHtml(state.aiGenerationNote)}</p>
            <strong>Titres alternatifs</strong>
            <div class="actions">${state.aiAlternativeTitles.map((title) => `<button type="button" data-ai-alternative-title="${escapeHtml(title)}">${escapeHtml(title)}</button>`).join("")}</div>
          </div>
        ` : ""}
        <div class="actions">
          <button type="button" class="primary" data-action="save"${state.articleSaving ? " disabled" : ""}>${state.articleSaving ? "Enregistrement…" : "Enregistrer"}</button>
          <button type="button" data-action="open-ai-dialog">Générer avec OpenAI</button>
          <button type="button" data-action="preview">Aperçu du site</button>
          <button type="button" data-action="markdown-preview">Aperçu Markdown</button>
          <button type="button" data-action="duplicate">Dupliquer</button>
          <button type="button" data-action="archive">Archiver</button>
          <button type="button" data-action="delete">Supprimer</button>
        </div>
      </div>
    `
    : selectedPage
      ? renderPageEditor(selectedPage)
      : `<div class="panel"><h2>Éditeur</h2><p>Sélectionnez un article ou une page pour commencer.</p></div>`;

  const isAnalytics = state.activeMenu === "analytics";
  const isPageView = isPageSettings || (Boolean(selectedPage) && state.activeMenu.startsWith("page-") && !isPageSettings);
  const contentHtml = isAnalytics || isMediaLibrary
    ? `
        <section class="content">
          <div class="topbar">
            <div>
              <div><strong>${escapeHtml(state.config?.name || "Les Boules Studio")}</strong></div>
              <div class="meta">${isMediaLibrary ? "Médiathèque et intégrité Storage" : "Statistiques et Analytics"}</div>
            </div>
          </div>
          <div class="panel">
            ${isMediaLibrary ? "" : "<h2>📊 Analytics</h2>"}
            ${editorHtml}
          </div>
        </section>
      `
    : isSettingsMenu
    ? `
        <section class="content">
          <div class="topbar">
            <div>
              <div><strong>${escapeHtml(state.config?.name || "Les Boules Studio")}</strong></div>
              <div class="meta">${isCategorySettings ? "Gestion des catégories" : isPublicationSettings ? "Paramètres de connexion FTP" : isHtaccessSettings ? "Configuration Apache .htaccess" : isStatisticsSettings ? "Paramètres de statistiques" : isOpenAISettings ? "Génération d’articles avec OpenAI" : isGoogleSettings ? "Paramètres Google" : isSocialSettings ? "Paramètres Social" : isThemeSettings ? "Présentation du site public" : "Équipe et stockage partagé"}</div>
            </div>
          </div>
          <div class="panel">
            ${editorHtml}
          </div>
        </section>
      `
    : isPageView
    ? `
        <section class="content">
          <div class="topbar">
            <div>
              <div><strong>${escapeHtml(state.config?.name || "Les Boules Studio")}</strong></div>
              <div class="meta">${isPageSettings ? "Gestion des pages" : `Page · ${escapeHtml(selectedPage?.frontMatter.title || "")}`}</div>
            </div>
          </div>
          <div class="panel">
            ${editorHtml}
          </div>
        </section>
      `
    : `
        <section class="content">
          <div class="topbar">
            <div>
              <div><strong>${escapeHtml(state.config?.name || "Les Boules Studio")}</strong></div>
              <div class="meta">${escapeHtml(state.config?.tagline || "")}</div>
            </div>
          </div>

          <div class="panel">
            <h2>Tableau de bord</h2>
            <div class="stats">
              <div class="stat"><b>${state.articles.length}</b><span>Articles</span></div>
              <div class="stat"><b>${state.articles.filter((a) => a.frontMatter.status === "published").length}</b><span>Publiés</span></div>
              <div class="stat"><b>${state.articles.filter((a) => a.frontMatter.status === "draft").length}</b><span>Brouillons</span></div>
              <div class="stat"><b>${state.articles.filter((a) => a.frontMatter.status === "scheduled").length}</b><span>Programmés</span></div>
            </div>
            <p class="status ${state.previewUrl ? "ok" : ""}">${state.previewUrl ? `Aperçu: ${state.previewUrl}` : state.buildStatus}</p>
          </div>

          <div class="grid article-editor-grid${state.articleListCollapsed ? " article-list-collapsed" : ""}${state.articleEditorCollapsed ? " article-editor-collapsed" : ""}" style="margin-top: 18px;">
            <div class="panel article-list-panel">
              <div class="panel-header">
                <h2>Liste des articles</h2>
                <button type="button" class="panel-toggle" data-action="article-list-toggle" aria-label="${state.articleListCollapsed ? "Déplier la liste des articles" : "Replier la liste des articles"}" title="${state.articleListCollapsed ? "Déplier la liste des articles" : "Replier la liste des articles"}">${state.articleListCollapsed ? "›" : "‹"}</button>
              </div>
              <div class="article-list-content">
                <div class="search-toolbar">
                  <input placeholder="Recherche locale…" id="search" value="${escapeHtml(state.filter)}">
                  <select id="article-status-filter" aria-label="Filtrer les articles par statut">
                    <option value="all"${state.statusFilter === "all" ? " selected" : ""}>Tous les statuts</option>
                    <option value="draft"${state.statusFilter === "draft" ? " selected" : ""}>Brouillons</option>
                    <option value="published"${state.statusFilter === "published" ? " selected" : ""}>Publiés</option>
                    <option value="scheduled"${state.statusFilter === "scheduled" ? " selected" : ""}>Programmés</option>
                    <option value="archived"${state.statusFilter === "archived" ? " selected" : ""}>Archivés</option>
                  </select>
                  <button type="button" data-action="clear-search">Effacer</button>
                </div>
                <table class="article-table">
                  <thead><tr>
                    ${renderArticleSortHeader("status", "Statut")}
                    ${renderArticleSortHeader("date", "Date")}
                    ${renderArticleSortHeader("title", "Titre")}
                    ${renderArticleSortHeader("category", "Rubrique")}
                    ${renderArticleSortHeader("updated", "Modifié")}
                  </tr></thead>
                  <tbody>
                    ${visibleArticles
                      .map(
                        (article) => `
                          <tr data-article-id="${escapeHtml(article.id)}">
                            <td>${escapeHtml(article.frontMatter.status)}</td>
                            <td>${escapeHtml(article.frontMatter.date.slice(0, 10))}</td>
                            <td><strong>${escapeHtml(article.frontMatter.title)}</strong></td>
                            <td>${escapeHtml(state.categories.find((category) => category.id === article.frontMatter.categoryId)?.name || article.frontMatter.categoryId)}</td>
                            <td>${escapeHtml(article.frontMatter.updated.slice(0, 16).replace("T", " "))}</td>
                          </tr>
                        `
                      )
                      .join("")}
                  </tbody>
                </table>
              </div>
            </div>
            <div class="panel article-editor-panel">
              <div class="panel-header">
                <h2>Éditeur</h2>
                <button type="button" class="panel-toggle" data-action="article-editor-toggle" aria-label="${state.articleEditorCollapsed ? "Déplier l’éditeur" : "Replier l’éditeur vers la droite"}" title="${state.articleEditorCollapsed ? "Déplier l’éditeur" : "Replier l’éditeur vers la droite"}">${state.articleEditorCollapsed ? "‹" : "›"}</button>
              </div>
              <div class="article-editor-content">${editorHtml}</div>
            </div>
          </div>
        </section>
      `;

  app.innerHTML = `
    <div class="shell${state.sidebarCollapsed ? " sidebar-collapsed" : ""}">
      <aside class="sidebar">
        <div class="sidebar-header">
          <h1>LES BOULES STUDIO</h1>
          <button type="button" class="sidebar-toggle" data-action="sidebar-toggle" aria-label="${state.sidebarCollapsed ? "Déplier le menu" : "Replier le menu"}" title="${state.sidebarCollapsed ? "Déplier le menu" : "Replier le menu"}">${state.sidebarCollapsed ? "›" : "‹"}</button>
        </div>
        <div class="sidebar-nav">
        <div class="nav-group">
          <h2>Tableau de bord</h2>
          <div class="nav-action-row">
            <button class="nav-action-main${state.activeMenu === "dashboard" ? " active" : ""}" data-nav="dashboard">Vue générale</button>
            <button type="button" class="nav-action-icon" data-action="refresh" aria-label="Rafraîchir" title="Rafraîchir">↻</button>
          </div>
          <button${state.activeMenu === "analytics" ? ' class="active"' : ""} data-nav="analytics">Statistiques</button>
          <button${state.activeMenu === "media" ? ' class="active"' : ""} data-nav="media">Médias</button>
        </div>
        <div class="nav-group">
          <h2>Articles</h2>
          <button${state.activeMenu === "new-article" ? ' class="active"' : ""} data-action="new-article">Nouvel article</button>
        </div>
        <div class="nav-group">
          <div class="nav-group-heading">
            <h2>Catégories</h2>
            <button type="button" class="nav-group-settings${state.activeMenu === "category-settings" ? " active" : ""}" data-nav="category-settings" aria-label="Gérer les catégories" title="Gérer les catégories">⚙</button>
          </div>
          <button data-filter-category="all"${state.activeMenu === "category-all" ? ' class="active"' : ""}>All</button>
          ${state.categories
            .map((category) => `<button data-filter-category="${escapeHtml(category.id)}"${state.activeMenu === `category-${category.id}` ? ' class="active"' : ""}>${escapeHtml(category.name)}</button>`)
            .join("")}
        </div>
        <div class="nav-group">
          <div class="nav-group-heading">
            <h2>Pages</h2>
            <button type="button" class="nav-group-settings${isPageSettings ? " active" : ""}" data-nav="page-settings" aria-label="Gérer les pages" title="Gérer les pages">⚙</button>
          </div>
          ${sortPages(state.pages)
            .map((page) => `<button data-page-id="${escapeHtml(page.id)}"${state.activeMenu === `page-${page.id}` ? ' class="active"' : ""}>${escapeHtml(page.frontMatter.title)}</button>`)
            .join("")}
        </div>
        <div class="nav-group">
          <h2>Publication</h2>
          <button${state.activeMenu === "build" ? ' class="active"' : ""} data-action="build">Générer le site</button>
          <button${state.activeMenu === "start-preview" ? ' class="active"' : ""} data-action="start-preview">Démarrer l’aperçu</button>
          <button data-action="open-push-dialog">Pousser</button>
        </div>
        </div>
      </aside>
      ${contentHtml}
    </div>
    ${state.settingsLauncherOpen ? '<div class="settings-launcher-backdrop" data-action="settings-launcher-close"></div>' : ""}
    <div class="settings-launcher">
      <button type="button" class="settings-launcher__toggle${isSettingsMenu ? " active" : ""}" data-action="settings-launcher-toggle" aria-label="Ouvrir les paramètres" title="Paramètres" aria-expanded="${state.settingsLauncherOpen}">⚙</button>
      ${state.settingsLauncherOpen ? `
        <div class="settings-launcher__panel" role="menu" aria-label="Paramètres">
          <div class="settings-launcher__heading">Paramètres</div>
          <button type="button" role="menuitem" data-nav="publication-settings"${isPublicationSettings ? ' class="active"' : ""}>Connexion FTP</button>
          <button type="button" role="menuitem" data-nav="htaccess-settings"${isHtaccessSettings ? ' class="active"' : ""}>.htaccess</button>
          <button type="button" role="menuitem" data-nav="statistics-settings"${isStatisticsSettings ? ' class="active"' : ""}>Statistiques</button>
          <button type="button" role="menuitem" data-nav="openai-settings"${isOpenAISettings ? ' class="active"' : ""}>OpenAI</button>
          <button type="button" role="menuitem" data-nav="google-settings"${isGoogleSettings ? ' class="active"' : ""}>Google</button>
          <button type="button" role="menuitem" data-nav="social-settings"${isSocialSettings ? ' class="active"' : ""}>Social</button>
          <button type="button" role="menuitem" data-nav="theme-settings"${isThemeSettings ? ' class="active"' : ""}>Thème du site</button>
          <button type="button" role="menuitem" data-nav="site-settings"${isSiteSettings ? ' class="active"' : ""}>Gestion du site</button>
          <button type="button" role="menuitem" data-nav="user-settings"${isUserSettings ? ' class="active"' : ""}>Utilisateurs</button>
          <div class="settings-launcher__separator"></div>
          <button type="button" class="settings-launcher__sign-out" role="menuitem" data-action="team-sign-out">Se déconnecter</button>
        </div>
      ` : ""}
    </div>
    ${state.progressNotice ? `
      <div class="app-notice-overlay">
        <div class="app-notice app-notice--progress">
          <div class="app-notice-title">${escapeHtml(state.progressNotice.title)}</div>
          <div class="progress-bar">
            <div class="progress-bar__fill" style="animation-duration:${Math.max(1, state.progressNotice.durationMs)}ms"></div>
          </div>
          <div class="app-notice-caption">${escapeHtml(state.progressNotice.caption)}</div>
        </div>
      </div>
    ` : ""}
    ${state.pushDialogOpen ? renderPushDialog() : ""}
    ${state.aiDialogOpen ? renderAIGenerationDialog() : ""}
  `;

  attachHandlers();
  updateArticleFieldValidation();
  initializeBodyEditors();
}

function getInputValue(field: string): string {
  const element = app.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[data-field="${field}"]`);
  if (!element) {
    return "";
  }
  if (element instanceof HTMLInputElement && element.type === "checkbox") {
    return element.checked ? "true" : "false";
  }
  return element.value;
}

function getPageInputValue(field: string): string {
  const element = app.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[data-page-field="${field}"]`);
  if (!element) {
    return "";
  }
  return element.value;
}

function getPublishFieldValue(field: string): string {
  const element = app.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[data-publish-field="${field}"]`);
  if (!element) {
    return "";
  }
  return element.value;
}

function getSiteFieldValue(field: string): string {
  const elements = Array.from(app.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[data-site-field="${field}"]`));
  const element = elements.find((candidate) =>
    candidate instanceof HTMLInputElement && candidate.type === "radio" ? candidate.checked : true
  );
  if (!element) {
    return "";
  }
  if (element instanceof HTMLInputElement && element.type === "checkbox") {
    return element.checked ? "true" : "false";
  }
  return element.value;
}

function currentPublishProfile(): PublishProfile | undefined {
  return sortPublishProfiles(state.publishConfig.profiles).find((profile) => profile.id === state.selectedPublishProfileId) || sortPublishProfiles(state.publishConfig.profiles)[0];
}

function updatePublishProfileFromForm(protocol: PublishProtocol): void {
  const profile = currentPublishProfile();
  if (!profile) {
    return;
  }

  Object.assign(profile, {
    name: getPublishFieldValue("name") || profile.name,
    protocol,
    host: getPublishFieldValue("host"),
    port: Number(getPublishFieldValue("port") || profile.port),
    username: getPublishFieldValue("username"),
    remotePath: getPublishFieldValue("remotePath"),
    publicUrl: getPublishFieldValue("publicUrl"),
    timeout: Number(getPublishFieldValue("timeout") || profile.timeout)
  });

  const passiveMode = getPublishFieldValue("passiveMode") as PublishMode;
  const sshFingerprint = getPublishFieldValue("sshFingerprint");
  const sshKeyPath = getPublishFieldValue("sshKeyPath");
  if (passiveMode) profile.passiveMode = passiveMode;
  if (protocol === "sftp") {
    profile.sshFingerprint = sshFingerprint || undefined;
    profile.sshKeyPath = sshKeyPath || undefined;
  }
}

function updateSelectedArticle(patch: Partial<ArticleFrontMatter> & { body?: string }): void {
  const selected = state.articles.find((article) => article.id === state.selectedArticleId);
  if (!selected) {
    return;
  }
  if (patch.body !== undefined) {
    selected.body = patch.body;
  }
  selected.frontMatter = {
    ...selected.frontMatter,
    ...patch,
    tags: typeof patch.tags === "string" ? patch.tags.split(",").map((tag) => tag.trim()).filter(Boolean) : selected.frontMatter.tags
  } as ArticleFrontMatter;
  if (!patch.slug && patch.title) {
    selected.frontMatter.slug = slugifyTitle(patch.title);
  }
  selected.frontMatter.updated = new Date().toISOString();
  state.dirty = true;
  state.saveNotice = undefined;
}

function showSaveNotice(message = "Enregistré"): void {
  state.saveNotice = message;
  state.saveError = undefined;
  if (saveNoticeTimer) {
    window.clearTimeout(saveNoticeTimer);
  }
  saveNoticeTimer = window.setTimeout(() => {
    state.saveNotice = undefined;
    saveNoticeTimer = undefined;
    render();
  }, 5000);
}

function renderSaveStatus(): string {
  if (state.saveError) {
    return `<div class="save-feedback status err" role="alert">${escapeHtml(state.saveError)}</div>`;
  }
  if (state.dirty) {
    return '<div class="save-feedback status warn" role="status">Modifications non enregistrées</div>';
  }
  if (state.saveNotice) {
    return `<div class="save-feedback status ok" role="status" aria-live="polite">${escapeHtml(state.saveNotice)}</div>`;
  }
  return "";
}

function renderSeoField(label: string, fieldName: string, value: string, pageField = false): string {
  const dataAttribute = pageField ? "data-page-field" : "data-field";
  const isSeoTitle = fieldName === "seo_title";
  const isSeoDescription = fieldName === "seo_description";
  const hint = fieldName === "seo_title"
    ? `Titre affiché dans Google et sur les réseaux sociaux. Entre ${ARTICLE_TITLE_MIN_LENGTH} et ${ARTICLE_TITLE_MAX_LENGTH} caractères, idéalement ${ARTICLE_TITLE_IDEAL_LENGTH}.`
    : `Petit texte affiché dans les résultats de recherche. Entre ${SEO_DESCRIPTION_MIN_LENGTH} et ${SEO_DESCRIPTION_MAX_LENGTH} caractères.`;
  const characterCount = isSeoTitle
    ? {
        fieldName,
        value,
        minLength: ARTICLE_TITLE_MIN_LENGTH,
        idealLength: ARTICLE_TITLE_IDEAL_LENGTH,
        maxLength: ARTICLE_TITLE_MAX_LENGTH
      }
    : isSeoDescription
      ? {
          fieldName,
          value,
          minLength: SEO_DESCRIPTION_MIN_LENGTH,
          maxLength: SEO_DESCRIPTION_MAX_LENGTH
        }
      : undefined;

  return `
    <label>
      ${renderHelpLabel(
        label,
        hint,
        "Aide SEO",
        characterCount
      )}
      <input ${dataAttribute}="${fieldName}"${characterCount ? ` minlength="${characterCount.minLength}" maxlength="${characterCount.maxLength}"` : ""} value="${escapeHtml(value)}">
    </label>
  `;
}

function renderHelpLabel(
  label: string,
  hint: string,
  ariaLabel = "Aide",
  characterCount?: {
    fieldName: string;
    value: string;
    minLength: number;
    idealLength?: number;
    maxLength: number;
  }
): string {
  const count = characterCount?.value.length || 0;
  const countStatus = characterCount
    ? characterCountDisplay(count, characterCount.minLength, characterCount.idealLength, characterCount.maxLength)
    : undefined;
  return `
    <span class="field-label-with-help">
      <span>${label}</span>
      <details class="field-help">
        <summary aria-label="${escapeHtml(ariaLabel)}">?</summary>
        <div class="field-help-popover">${escapeHtml(hint)}</div>
      </details>
      ${characterCount && countStatus ? `<span class="character-count ${countStatus.className}" data-character-count-for="${escapeHtml(characterCount.fieldName)}" data-character-count-min="${characterCount.minLength}"${characterCount.idealLength === undefined ? "" : ` data-character-count-ideal="${characterCount.idealLength}"`} data-character-count-max="${characterCount.maxLength}">${escapeHtml(countStatus.text)}</span>` : ""}
    </span>
  `;
}

function characterCountDisplay(
  count: number,
  minLength: number,
  idealLength: number | undefined,
  maxLength: number
): { text: string; className: string } {
  if (count < minLength) {
    return {
      text: `${count}/${maxLength} · trop court (min. ${minLength})`,
      className: "character-count--invalid"
    };
  }
  if (count > maxLength) {
    return {
      text: `${count}/${maxLength} · trop long`,
      className: "character-count--invalid"
    };
  }
  if (idealLength !== undefined && count === idealLength) {
    return {
      text: `${count}/${maxLength} · idéal`,
      className: "character-count--ideal"
    };
  }
  return {
    text: idealLength === undefined
      ? `${count}/${maxLength} · valide`
      : `${count}/${maxLength} · valide (idéal ${idealLength})`,
    className: "character-count--valid"
  };
}

function updateCharacterCount(field: HTMLInputElement | HTMLTextAreaElement): void {
  const fieldName = field.dataset.field || field.dataset.pageField || field.dataset.siteField;
  if (!fieldName) return;
  app.querySelectorAll<HTMLElement>("[data-character-count-for]").forEach((counter) => {
    if (counter.dataset.characterCountFor !== fieldName) return;
    const minLength = Number(counter.dataset.characterCountMin);
    const idealLength = counter.dataset.characterCountIdeal === undefined
      ? undefined
      : Number(counter.dataset.characterCountIdeal);
    const maxLength = Number(counter.dataset.characterCountMax);
    const status = characterCountDisplay(field.value.length, minLength, idealLength, maxLength);
    counter.textContent = status.text;
    counter.classList.remove("character-count--invalid", "character-count--valid", "character-count--ideal");
    counter.classList.add(status.className);
  });
}

function articleWordCountText(count: number): string {
  const label = `${count} mot${count === 1 ? "" : "s"}`;
  if (isArticleWordCountRecommended(count)) return `${label} · valide (cible ${ARTICLE_WORD_COUNT_TARGET})`;
  if (count < ARTICLE_WORD_COUNT_MIN) return `${label} · trop court (min. ${ARTICLE_WORD_COUNT_MIN})`;
  return `${label} · trop long (max. ${ARTICLE_WORD_COUNT_MAX})`;
}

function renderArticleWordCount(markdown: string): string {
  const count = countMarkdownWords(markdown);
  const className = isArticleWordCountRecommended(count) ? "word-count--valid" : "word-count--invalid";
  return `<span class="word-count ${className}" data-article-word-count>${articleWordCountText(count)}</span>`;
}

function updateArticleWordCount(markdown: string): void {
  const counter = app.querySelector<HTMLElement>("[data-article-word-count]");
  if (!counter) return;
  const count = countMarkdownWords(markdown);
  counter.textContent = articleWordCountText(count);
  counter.classList.toggle("word-count--valid", isArticleWordCountRecommended(count));
  counter.classList.toggle("word-count--invalid", !isArticleWordCountRecommended(count));
}

type ArticleFieldValidation = "valid" | "invalid" | "neutral";

function articleFieldValidation(fieldName: string, value: string): ArticleFieldValidation {
  const trimmed = value.trim();
  switch (fieldName) {
    case "title":
    case "seo_title":
      return trimmed.length >= ARTICLE_TITLE_MIN_LENGTH && trimmed.length <= ARTICLE_TITLE_MAX_LENGTH
        ? "valid"
        : "invalid";
    case "seo_description":
      return trimmed.length >= SEO_DESCRIPTION_MIN_LENGTH && trimmed.length <= SEO_DESCRIPTION_MAX_LENGTH
        ? "valid"
        : "invalid";
    case "slug": {
      const duplicate = state.articles.some((article) =>
        article.id !== state.selectedArticleId && article.frontMatter.slug === trimmed
      );
      return isValidSlug(trimmed) && !duplicate ? "valid" : "invalid";
    }
    case "category":
      return state.categories.some((category) => category.id === value && category.enabled)
        ? "valid"
        : "invalid";
    case "status":
      return ["draft", "published", "scheduled", "archived"].includes(value) ? "valid" : "invalid";
    case "date":
      return trimmed && !Number.isNaN(Date.parse(trimmed)) ? "valid" : "invalid";
    case "author":
      return trimmed ? "valid" : "invalid";
    case "locale":
      return isEuropeanLocale(value) ? "valid" : "invalid";
    case "image": {
      if (!trimmed) return "neutral";
      const normalized = trimmed.replace(/^\/+/, "").replaceAll("\\", "/");
      return state.mediaAssets.some((asset) =>
        asset.publicPath.replace(/^\/+/, "").replaceAll("\\", "/") === normalized
      ) ? "valid" : "invalid";
    }
    case "image_alt":
      return getInputValue("image").trim() ? (trimmed ? "valid" : "invalid") : "neutral";
    default:
      return "neutral";
  }
}

function updateArticleFieldValidation(): void {
  app.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-field]").forEach((field) => {
    if (field instanceof HTMLInputElement && field.type === "checkbox") return;
    const fieldName = field.dataset.field;
    if (!fieldName) return;
    const validation = articleFieldValidation(fieldName, field.value);
    field.classList.toggle("article-field--valid", validation === "valid");
    field.classList.toggle("article-field--invalid", validation === "invalid");
    if (validation === "invalid") field.setAttribute("aria-invalid", "true");
    else field.removeAttribute("aria-invalid");
  });
}

function renderArticleSortHeader(column: ArticleSortColumn, label: string): string {
  const active = state.sortColumn === column;
  const nextDirection = active && state.sortDirection === "asc" ? "décroissant" : "croissant";
  return `
    <th aria-sort="${active ? (state.sortDirection === "asc" ? "ascending" : "descending") : "none"}">
      <button type="button" class="article-sort${active ? " active" : ""}" data-sort-column="${column}" title="Trier par ${escapeHtml(label)}, ordre ${nextDirection}">
        <span>${escapeHtml(label)}</span><span class="article-sort__indicator" aria-hidden="true">${active ? (state.sortDirection === "asc" ? "↑" : "↓") : "↕"}</span>
      </button>
    </th>
  `;
}

function sortArticles(articles: ArticleRecord[]): ArticleRecord[] {
  const items = [...articles];
  const valueFor = (article: ArticleRecord): string => {
    switch (state.sortColumn) {
      case "status": return article.frontMatter.status;
      case "title": return article.frontMatter.title;
      case "category": return state.categories.find((category) => category.id === article.frontMatter.categoryId)?.name || article.frontMatter.categoryId;
      case "updated": return article.frontMatter.updated;
      case "date": return article.frontMatter.date;
    }
  };
  const direction = state.sortDirection === "asc" ? 1 : -1;
  return items.sort((left, right) => {
    const comparison = valueFor(left).localeCompare(valueFor(right), "fr", { sensitivity: "base" });
    return comparison * direction || left.id.localeCompare(right.id);
  });
}

function destroyBodyEditors(): void {
  articleBodyEditor?.destroy();
  pageBodyEditor?.destroy();
  articleBodyEditor = undefined;
  pageBodyEditor = undefined;
}

function editorModeButtonClass(mode: "wysiwyg" | "markdown"): string {
  return state.editorMode === mode ? ' class="active"' : "";
}

function renderEditorModeSwitch(): string {
  return `
    <div class="editor-mode-switch" role="tablist" aria-label="Mode d’édition">
      <button type="button" data-editor-mode="wysiwyg"${editorModeButtonClass("wysiwyg")}>Visuel</button>
      <button type="button" data-editor-mode="markdown"${editorModeButtonClass("markdown")}>Markdown</button>
    </div>
  `;
}

function applyEditorMode(mode: "wysiwyg" | "markdown"): void {
  state.editorMode = mode;
  if (articleBodyEditor) {
    if (mode === "markdown" && !articleBodyEditor.isMarkdownMode()) {
      articleBodyEditor.changeMode(mode, true);
    }
    if (mode === "wysiwyg" && !articleBodyEditor.isWysiwygMode()) {
      articleBodyEditor.changeMode(mode, true);
    }
  }
  if (pageBodyEditor) {
    if (mode === "markdown" && !pageBodyEditor.isMarkdownMode()) {
      pageBodyEditor.changeMode(mode, true);
    }
    if (mode === "wysiwyg" && !pageBodyEditor.isWysiwygMode()) {
      pageBodyEditor.changeMode(mode, true);
    }
  }
  app.querySelectorAll<HTMLButtonElement>("[data-editor-mode]").forEach((button) => {
    button.classList.toggle("active", button.dataset.editorMode === mode);
  });
}

function createBodyEditor(container: HTMLElement, initialValue: string, placeholder: string, onChange: (markdown: string) => void): Editor {
  const editor = new Editor({
    el: container,
    height: "420px",
    minHeight: "320px",
    initialEditType: state.editorMode,
    initialValue,
    hideModeSwitch: true,
    previewStyle: "tab",
    usageStatistics: false,
    placeholder,
    language: "fr-FR",
    autofocus: false,
    hooks: {
      addImageBlobHook: async (blob, callback) => {
        const fileName = "name" in blob && typeof blob.name === "string" && blob.name ? blob.name : "image.png";
        const bytes = new Uint8Array(await blob.arrayBuffer());
        const savedPath = await window.lesBoulesStudio.saveMediaAsset(fileName, bytes, blob.type);
        state.mediaAssets = await window.lesBoulesStudio.listMediaAssets();
        callback(`/${savedPath.replace(/^\/+/, "")}`, fileName);
      }
    }
  });

  editor.on("change", () => {
    onChange(editor.getMarkdown());
  });
  editor.on("changeMode", () => {
    if (editor.isMarkdownMode()) {
      applyEditorMode("markdown");
      return;
    }
    if (editor.isWysiwygMode()) {
      applyEditorMode("wysiwyg");
    }
  });

  return editor;
}

function getArticleBodyValue(): string {
  if (articleBodyEditor) {
    return articleBodyEditor.getMarkdown();
  }
  return getInputValue("body");
}

function getPageBodyValue(): string {
  if (pageBodyEditor) {
    return pageBodyEditor.getMarkdown();
  }
  return getPageInputValue("body");
}

function initializeBodyEditors(): void {
  const articleContainer = app.querySelector<HTMLElement>("[data-body-editor='article']");
  if (articleContainer && state.selectedArticleId) {
    const selected = state.articles.find((article) => article.id === state.selectedArticleId);
    if (selected) {
      articleBodyEditor = createBodyEditor(
        articleContainer,
        selected.body,
        "Rédigez votre article…",
        (markdown) => {
          selected.body = markdown;
          selected.frontMatter.updated = new Date().toISOString();
          state.dirty = true;
          state.saveNotice = undefined;
          updateArticleWordCount(markdown);
        }
      );
    }
  }

  const pageContainer = app.querySelector<HTMLElement>("[data-body-editor='page']");
  if (pageContainer && state.selectedPageId) {
    const selected = sortPages(state.pages).find((page) => page.id === state.selectedPageId);
    if (selected) {
      pageBodyEditor = createBodyEditor(
        pageContainer,
        selected.body,
        "Rédigez le contenu de la page…",
        (markdown) => {
          selected.body = markdown;
          state.dirty = true;
          state.saveNotice = undefined;
        }
      );
    }
  }

  applyEditorMode(state.editorMode);
}

async function saveSelectedArticle(): Promise<void> {
  const selected = captureSelectedArticleForm();
  if (!selected) {
    return;
  }
  state.articleSaving = true;
  state.saveError = undefined;
  state.saveNotice = undefined;
  render();
  try {
    const saved = await window.lesBoulesStudio.saveArticle(selected);
    Object.assign(selected, saved);
    if (
      state.featuredArticleSettings.articleId === saved.id
      && !["published", "scheduled"].includes(saved.frontMatter.status)
    ) {
      state.featuredArticleSettings = await window.lesBoulesStudio.getFeaturedArticleSettings();
    }
    state.dirty = false;
    state.articleSaving = false;
    showSaveNotice(`Article enregistré à ${new Date().toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    })}`);
    render();
  } catch (error) {
    state.articleSaving = false;
    state.saveError = error instanceof Error ? error.message : String(error);
    state.dirty = true;
    render();
  }
}

function captureSelectedArticleForm(): ArticleRecord | undefined {
  const selected = state.articles.find((article) => article.id === state.selectedArticleId);
  if (!selected) return undefined;
  selected.frontMatter = {
    ...selected.frontMatter,
    title: getInputValue("title"),
    slug: getInputValue("slug") || slugifyTitle(getInputValue("title")),
    categoryId: getInputValue("category"),
    date: getInputValue("date"),
    author: getInputValue("author"),
    status: getInputValue("status") as ArticleStatus,
    image: getInputValue("image") || undefined,
    image_alt: getInputValue("image_alt") || undefined,
    tags: getInputValue("tags")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean),
    satire: (app.querySelector<HTMLInputElement>('[data-field="satire"]')?.checked ?? false),
    seo_title: getInputValue("seo_title") || undefined,
    seo_description: getInputValue("seo_description") || undefined,
    locale: getInputValue("locale") || DEFAULT_ARTICLE_LOCALE,
    updated: new Date().toISOString()
  };
  selected.body = getArticleBodyValue();
  return selected;
}

async function saveSelectedPage(): Promise<void> {
  const selected = sortPages(state.pages).find((page) => page.id === state.selectedPageId);
  if (!selected) {
    return;
  }
  const page: PageRecord = {
    ...selected,
    frontMatter: {
      ...selected.frontMatter,
      title: getPageInputValue("title"),
      slug: getPageInputValue("slug") || slugifyTitle(getPageInputValue("title")),
      order: getPageInputValue("order") ? Number(getPageInputValue("order")) : undefined,
      seo_title: getPageInputValue("seo_title") || undefined,
      seo_description: getPageInputValue("seo_description") || undefined
    },
    body: getPageBodyValue()
  };

  try {
    const saved = await window.lesBoulesStudio.savePage(page);
    Object.assign(selected, saved);
    state.dirty = false;
    state.selectedPageId = saved.id;
    showSaveNotice();
    await refresh();
  } catch (error) {
    state.saveError = error instanceof Error ? error.message : String(error);
    state.dirty = true;
    render();
  }
}

async function openMarkdownPreview(): Promise<void> {
  const selectedArticle = state.articles.find((article) => article.id === state.selectedArticleId);
  if (selectedArticle) {
    const title = getInputValue("title") || selectedArticle.frontMatter.title;
    await window.lesBoulesStudio.openMarkdownPreview(title, markdownToHtml(getArticleBodyValue()));
    return;
  }

  const selectedPage = sortPages(state.pages).find((page) => page.id === state.selectedPageId);
  if (selectedPage) {
    const title = getPageInputValue("title") || selectedPage.frontMatter.title;
    await window.lesBoulesStudio.openMarkdownPreview(title, markdownToHtml(getPageBodyValue()));
  }
}

async function savePublishSettings(): Promise<void> {
  const profile = currentPublishProfile();
  if (!profile) {
    return;
  }
  const protocol = getPublishFieldValue("protocol") as PublishProtocol;

  const updatedProfile: PublishProfile = {
    ...profile,
    name: getPublishFieldValue("name") || profile.name,
    protocol,
    host: getPublishFieldValue("host"),
    port: Number(getPublishFieldValue("port") || profile.port),
    username: getPublishFieldValue("username"),
    remotePath: getPublishFieldValue("remotePath"),
    publicUrl: getPublishFieldValue("publicUrl"),
    timeout: Number(getPublishFieldValue("timeout") || profile.timeout),
    passiveMode: protocol === "sftp" ? profile.passiveMode : (getPublishFieldValue("passiveMode") as PublishMode) || profile.passiveMode,
    sshFingerprint: protocol === "sftp" ? getPublishFieldValue("sshFingerprint") || undefined : profile.sshFingerprint,
    sshKeyPath: protocol === "sftp" ? getPublishFieldValue("sshKeyPath") || undefined : profile.sshKeyPath
  };

  const profiles = state.publishConfig.profiles.map((item) => (item.id === profile.id ? updatedProfile : item));
  state.publishConfig = {
    profiles,
    htaccessEnabled: state.publishConfig.htaccessEnabled,
    htaccessContent: state.publishConfig.htaccessContent
  };
  state.publishBusy = true;
  state.saveError = undefined;
  render();
  try {
    const saved = await window.lesBoulesStudio.savePublishConfig(state.publishConfig);
    state.publishConfig = saved;
    state.selectedPublishProfileId = updatedProfile.id;
    state.dirty = false;
    showSaveNotice();
  } catch (error) {
    state.saveError = error instanceof Error ? error.message : String(error);
    state.dirty = true;
  } finally {
    state.publishBusy = false;
    render();
  }
}

async function saveHtaccessSettings(): Promise<void> {
  const htaccessEnabled = app.querySelector<HTMLInputElement>('[data-publish-field="htaccessEnabled"]')?.checked ?? false;
  const htaccessContent = getPublishFieldValue("htaccessContent");
  try {
    state.publishConfig = await window.lesBoulesStudio.savePublishConfig({
      ...state.publishConfig,
      htaccessEnabled,
      htaccessContent
    });
    state.dirty = false;
    showSaveNotice();
    render();
  } catch (error) {
    state.saveError = error instanceof Error ? error.message : String(error);
    state.dirty = true;
    render();
  }
}

async function saveSiteSettings(): Promise<void> {
  if (!state.config) {
    return;
  }

  async function deleteTheme(themeId: string): Promise<void> {
    if (!state.config || themeId === "v6" || !isSiteThemeId(themeId)) {
      state.saveError = "Ce thème ne peut pas être supprimé.";
      render();
      return;
    }
    const theme = SITE_THEMES.find((candidate) => candidate.id === themeId);
    if (!theme || !confirm(`Supprimer le thème « ${theme.name} » de ce workspace ?`)) {
      return;
    }
    const wasActive = state.config.template === themeId;
    const disabledThemes = [...new Set([...(state.config.disabledThemes || []), themeId])];
    const nextConfig: SiteConfig = {
      ...state.config,
      template: wasActive ? "v6" : state.config.template,
      disabledThemes
    };
    try {
      state.config = await window.lesBoulesStudio.saveConfig(nextConfig);
      state.dirty = false;
      state.saveError = undefined;
      showSaveNotice(wasActive
        ? "Thème supprimé. Le thème classique est actif."
        : "Thème supprimé.");
      render();
    } catch (error) {
      state.saveError = error instanceof Error ? error.message : String(error);
      render();
    }
  }

  const nextConfig: SiteConfig = { ...state.config };

  if (state.activeMenu === "statistics-settings") {
    nextConfig.analyticsProvider = (getSiteFieldValue("analyticsProvider") as SiteConfig["analyticsProvider"]) || state.config.analyticsProvider || "supabase";
    nextConfig.analyticsEnabled = getSiteFieldValue("analyticsEnabled") === "true";
    nextConfig.showViewCounter = getSiteFieldValue("showViewCounter") === "true";
    nextConfig.supabaseUrl = getSiteFieldValue("supabaseUrl").trim();
    nextConfig.supabaseAnonKey = getSiteFieldValue("supabaseAnonKey").trim();
  }

  if (state.activeMenu === "google-settings") {
    const seoDescription = getSiteFieldValue("seoDescription").trim();
    if (seoDescription.length < SEO_DESCRIPTION_MIN_LENGTH || seoDescription.length > SEO_DESCRIPTION_MAX_LENGTH) {
      state.saveError = `La description SEO doit contenir entre ${SEO_DESCRIPTION_MIN_LENGTH} et ${SEO_DESCRIPTION_MAX_LENGTH} caractères (${seoDescription.length} actuellement).`;
      render();
      return;
    }
    nextConfig.seoDescription = seoDescription;
    nextConfig.googleAnalyticsEnabled = getSiteFieldValue("googleAnalyticsEnabled") === "true";
    nextConfig.googleAnalyticsId = getSiteFieldValue("googleAnalyticsId").trim();
  }

  if (state.activeMenu === "social-settings") {
    nextConfig.socialSharingEnabled = getSiteFieldValue("socialSharingEnabled") === "true";
    nextConfig.socialNetworks = Array.from(app.querySelectorAll<HTMLInputElement>("[data-social-network]:checked"))
      .map((element) => element.dataset.socialNetwork || "")
      .filter(Boolean);
  }

  if (state.activeMenu === "theme-settings") {
    const template = getSiteFieldValue("template");
    if (!isSiteThemeId(template)) {
      state.saveError = "Le thème sélectionné n’est pas pris en charge par cette version de LBStudio.";
      render();
      return;
    }
    nextConfig.template = template;
  }

  state.config = await window.lesBoulesStudio.saveConfig(nextConfig);
  state.dirty = false;
  showSaveNotice();
  render();
}

async function testCurrentSupabaseConnection(): Promise<void> {
  try {
    const result = await window.lesBoulesStudio.testAnalyticsConnection();
    state.supabaseConnectionStatus = {
      ok: true,
      message: `Fonction Edge connectée. ${result.count} vue(s) accessible(s) pour ce workspace.`
    };
    render();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    state.supabaseConnectionStatus = {
      ok: false,
      message: `Échec de connexion Supabase : ${message}`
    };
    render();
  }
}

async function openCurrentSupabase(): Promise<void> {
  const rawUrl = getSiteFieldValue("supabaseUrl").trim() || state.config?.supabaseUrl || "";
  if (!rawUrl) {
    state.supabaseConnectionStatus = {
      ok: false,
      message: "Impossible d’ouvrir Supabase : URL manquante."
    };
    render();
    return;
  }

  try {
    const url = new URL(rawUrl);
    const projectRef = url.hostname.split(".")[0];
    const dashboardUrl = projectRef
      ? `https://supabase.com/dashboard/project/${projectRef}`
      : "https://supabase.com/dashboard";
    await window.lesBoulesStudio.openExternal(dashboardUrl);
  } catch {
    state.supabaseConnectionStatus = {
      ok: false,
      message: "Impossible d’ouvrir Supabase : URL invalide."
    };
    render();
  }
}

async function pushCurrentSite(): Promise<void> {
  const profile = currentPublishProfile();
  const validation = pushValidation(profile);
  if (!profile || !validation.valid) {
    state.pushStatus = {
      ok: false,
      message: validation.message || "Configuration de publication incomplète."
    };
    render();
    return;
  }

  if (!state.preflightReport?.canPublish) {
    state.pushStatus = {
      ok: false,
      message: "Corrigez les erreurs du contrôle avant de publier."
    };
    render();
    return;
  }
  const confirmWarnings = state.preflightReport.warnings === 0
    || window.confirm(`Le contrôle contient ${state.preflightReport.warnings} avertissement(s). Continuer la publication ?`);
  if (!confirmWarnings) return;

  state.pushRunning = true;
  state.pushStatus = undefined;
  render();

  try {
    const result = await window.lesBoulesStudio.pushPublishProfile({
      profileId: profile.id,
      secrets: {
        password: state.pushPassword.trim() || undefined,
        passphrase: state.pushPassphrase.trim() || undefined
      },
      confirmWarnings
    });
    state.pushStatus = {
      ok: true,
      message: `${result.uploadedFiles} fichier(s) envoyé(s), ${result.unchangedFiles} inchangé(s) et ${result.removedFiles} supprimé(s) sur ${result.host}${result.remotePath}.`
    };
    state.publicationPlan = undefined;
    state.publicationHistory = await window.lesBoulesStudio.listPublicationHistory(profile.id);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    state.pushStatus = {
      ok: false,
      message
    };
  } finally {
    state.pushRunning = false;
    render();
  }
}

async function refreshPublishInspection(connectRemote = false): Promise<void> {
  const profile = currentPublishProfile();
  if (!profile) return;
  state.pushRunning = true;
  state.pushStatus = undefined;
  render();
  try {
    state.preflightReport = await window.lesBoulesStudio.runPreflight(profile.id);
    state.publicationHistory = await window.lesBoulesStudio.listPublicationHistory(profile.id);
    if (connectRemote) {
      state.publicationPlan = await window.lesBoulesStudio.planPublishProfile({
        profileId: profile.id,
        secrets: {
          password: state.pushPassword.trim() || undefined,
          passphrase: state.pushPassphrase.trim() || undefined
        }
      });
    }
  } catch (error) {
    state.pushStatus = {
      ok: false,
      message: error instanceof Error ? error.message : String(error)
    };
  } finally {
    state.pushRunning = false;
    render();
  }
}

async function restoreSelectedPublication(publicationId: string): Promise<void> {
  const profile = currentPublishProfile();
  if (!profile || !window.confirm("Restaurer cet instantané sur le serveur ? Cette opération sera enregistrée comme une nouvelle publication.")) return;
  state.pushRunning = true;
  state.pushStatus = undefined;
  render();
  try {
    const result = await window.lesBoulesStudio.restorePublication({
      profileId: profile.id,
      publicationId,
      secrets: {
        password: state.pushPassword.trim() || undefined,
        passphrase: state.pushPassphrase.trim() || undefined
      }
    });
    state.pushStatus = {
      ok: true,
      message: `Instantané restauré : ${result.uploadedFiles} fichier(s) envoyé(s), ${result.removedFiles} supprimé(s).`
    };
    state.publicationPlan = undefined;
    state.publicationHistory = await window.lesBoulesStudio.listPublicationHistory(profile.id);
  } catch (error) {
    state.pushStatus = {
      ok: false,
      message: error instanceof Error ? error.message : String(error)
    };
  } finally {
    state.pushRunning = false;
    render();
  }
}

async function addPublishProfile(duplicateCurrent = false): Promise<void> {
  const base = duplicateCurrent && currentPublishProfile() ? currentPublishProfile()! : defaultPublishProfile();
  const nextName = duplicateCurrent && currentPublishProfile()
    ? `${base.name} copie`
    : state.publishConfig.profiles.length === 0
      ? "Production"
      : `Profil ${state.publishConfig.profiles.length + 1}`;
  const profile: PublishProfile = {
    ...base,
    id: uniquePublishProfileId(nextName, state.publishConfig.profiles),
    name: nextName
  };
  state.publishConfig = {
    ...state.publishConfig,
    profiles: [...state.publishConfig.profiles, profile]
  };
  state.selectedPublishProfileId = profile.id;
  state.activeMenu = "publication-settings";
  state.dirty = true;
  state.saveNotice = undefined;
  state.saveError = undefined;
  render();
}

async function deleteCurrentPublishProfile(): Promise<void> {
  const profile = currentPublishProfile();
  if (!profile) {
    return;
  }
  if (!confirm(`Supprimer le profil « ${profile.name} » ?`)) return;
  const previousConfig = state.publishConfig;
  const previousSelection = state.selectedPublishProfileId;
  const profiles = previousConfig.profiles.filter((item) => item.id !== profile.id);
  state.publishConfig = { ...previousConfig, profiles };
  state.selectedPublishProfileId = profiles[0]?.id;
  state.publishBusy = true;
  state.dirty = true;
  state.saveNotice = undefined;
  state.saveError = undefined;
  render();
  try {
    state.publishConfig = await window.lesBoulesStudio.savePublishConfig(state.publishConfig);
    state.dirty = false;
    showSaveNotice("Profil supprimé");
  } catch (error) {
    state.publishConfig = previousConfig;
    state.selectedPublishProfileId = previousSelection;
    state.saveError = error instanceof Error ? error.message : String(error);
    state.dirty = true;
  } finally {
    state.publishBusy = false;
    render();
  }
}

async function saveAutomaticPublishing(): Promise<void> {
  const profileId = app.querySelector<HTMLSelectElement>('[data-automatic-publish-field="profileId"]')?.value || "";
  const enabled = app.querySelector<HTMLInputElement>('[data-automatic-publish-field="enabled"]')?.checked === true;
  const password = app.querySelector<HTMLInputElement>('[data-automatic-publish-field="password"]')?.value.trim();
  const passphrase = app.querySelector<HTMLInputElement>('[data-automatic-publish-field="passphrase"]')?.value.trim();
  state.publishBusy = true;
  state.saveNotice = undefined;
  state.saveError = undefined;
  render();
  try {
    await window.lesBoulesStudio.saveAutomaticPublishSettings({
      enabled,
      profileId,
      password: password || undefined,
      passphrase: passphrase || undefined
    });
    state.automaticPublishSettings = await window.lesBoulesStudio.getAutomaticPublishSettings();
    state.scheduledPublishJobs = await window.lesBoulesStudio.listScheduledPublishJobs();
    showSaveNotice(enabled ? "Publication automatique activée" : "Publication automatique enregistrée");
  } catch (error) {
    state.saveError = error instanceof Error ? error.message : String(error);
  } finally {
    state.publishBusy = false;
    render();
  }
}

async function deleteAutomaticPublishingSecret(): Promise<void> {
  const profileId = state.automaticPublishSettings.profileId;
  if (!profileId || !confirm("Supprimer le secret automatique et désactiver la publication programmée ?")) return;
  state.publishBusy = true;
  try {
    await window.lesBoulesStudio.deleteAutomaticPublishSecret(profileId);
    state.automaticPublishSettings = {
      enabled: false,
      profileId,
      secretConfigured: false,
      workerActive: state.automaticPublishSettings.workerActive,
      workerLastSeenAt: state.automaticPublishSettings.workerLastSeenAt
    };
    showSaveNotice("Secret automatique supprimé");
  } catch (error) {
    state.saveError = error instanceof Error ? error.message : String(error);
  } finally {
    state.publishBusy = false;
    render();
  }
}

async function retryAutomaticPublishJob(jobId: string): Promise<void> {
  try {
    await window.lesBoulesStudio.retryScheduledPublishJob(jobId);
    state.scheduledPublishJobs = await window.lesBoulesStudio.listScheduledPublishJobs();
    showSaveNotice("Tâche remise en file d’attente");
  } catch (error) {
    state.saveError = error instanceof Error ? error.message : String(error);
    render();
  }
}

async function loadAnalyticsData(): Promise<void> {
  try {
    if (!state.config || state.config.analyticsProvider !== "supabase" || state.config.analyticsEnabled === false) {
      state.analyticsEvents = [];
      state.analyticsData = summarizeAnalyticsEvents([]);
      render();
      return;
    }

    state.analyticsEvents = await window.lesBoulesStudio.listAnalyticsEvents();
    state.analyticsData = summarizeAnalyticsEvents(state.analyticsEvents);
    render();
    renderAnalyticsChart(filterAnalyticsEvents(state.analyticsEvents, state.analyticsRange, state.analyticsQuery));
  } catch (error) {
    console.error("Error loading analytics:", error);
    state.analyticsEvents = [];
    state.analyticsData = {
      total_views: 0,
      unique_visitors: 0,
      total_articles: 0,
      articles: [],
      timeline: []
    };
    render();
  }
}

function renderAnalyticsChart(events: AnalyticsEvent[]): void {
  const canvas = app.querySelector<HTMLCanvasElement>("#views-timeline");
  if (!canvas) {
    return;
  }

  const timeline = summarizeAnalyticsEvents(events).timeline;

  try {
    if (analyticsChart && typeof (analyticsChart as { destroy?: () => void }).destroy === "function") {
      (analyticsChart as { destroy: () => void }).destroy();
    }

    analyticsChart = new Chart(canvas, {
      type: "bar",
      data: {
        labels: timeline.map((d: any) => new Date(d.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })),
        datasets: [
          {
            label: "Vues",
            data: timeline.map((d: any) => d.count),
            backgroundColor: "#FF6B35"
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          y: { beginAtZero: true }
        }
      }
    });
  } catch (error) {
    console.error("Error rendering chart:", error);
  }
}

function rerenderAnalyticsChart(): void {
  if (state.activeMenu !== "analytics") {
    return;
  }

  window.setTimeout(() => {
    renderAnalyticsChart(filterAnalyticsEvents(state.analyticsEvents, state.analyticsRange, state.analyticsQuery));
  }, 0);
}

function downloadAnalyticsCsv(events: AnalyticsEvent[]): void {
  const rows = [
    ["article_id", "slug", "article_title", "visitor_id", "referrer", "device_type", "viewed_at"],
    ...events.map((event) => [
      event.article_id,
      event.article_slug,
      event.article_title || "",
      event.visitor_id,
      event.referrer || "",
      event.device_type || "",
      event.viewed_at
    ])
  ];

  const csv = rows.map((row) => row.map((value) => escapeCsv(String(value))).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `analytics-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function uniqueArticleSlug(title: string, currentArticleId: string): string {
  const base = slugifyTitle(title) || "article";
  const existing = new Set(
    state.articles
      .filter((article) => article.id !== currentArticleId)
      .map((article) => article.frontMatter.slug)
  );
  if (!existing.has(base)) return base;
  let suffix = 2;
  while (existing.has(`${base}-${suffix}`)) suffix += 1;
  return `${base}-${suffix}`;
}

function applyGeneratedArticle(generated: GeneratedArticleContent): void {
  const article = state.articles.find((item) => item.id === state.selectedArticleId);
  if (!article) throw new Error("Aucun brouillon n’est ouvert.");
  article.frontMatter.title = generated.title.trim();
  article.frontMatter.slug = uniqueArticleSlug(generated.title, article.id);
  const suggestedCategory = state.categories.find((category) => category.slug === generated.suggestedCategory);
  if (!suggestedCategory) {
    throw new Error(`La catégorie suggérée « ${generated.suggestedCategory} » n’existe pas.`);
  }
  article.frontMatter.categoryId = suggestedCategory.id;
  article.frontMatter.tags = generated.tags.map((tag) => tag.trim()).filter(Boolean);
  article.frontMatter.status = "draft";
  article.frontMatter.satire = true;
  article.frontMatter.seo_title = generated.seoTitle.trim();
  article.frontMatter.seo_description = generated.seoDescription.trim();
  article.frontMatter.updated = new Date().toISOString();
  article.excerpt = generated.standfirst.trim();
  article.body = [
    generated.standfirst.trim(),
    generated.bodyMarkdown.trim(),
    "---",
    "SATIRE — Les faits sont sérieux. Nous, nettement moins.",
    generated.satireNote.trim()
  ].join("\n\n");
  state.aiGenerationNote = generated.noteToEditor.trim();
  state.aiAlternativeTitles = generated.alternativeTitles;
  state.aiGeneratedArticleId = article.id;
  state.dirty = true;
}

async function generateCurrentArticleWithAI(): Promise<void> {
  if (!state.aiTheme.trim() || !state.openAISettings.prompt.trim() || state.aiGenerating) return;
  state.aiGenerating = true;
  state.aiGenerationStatus = { ok: true, message: "OpenAI recherche les faits et rédige le brouillon…" };
  render();
  try {
    const generated = await window.lesBoulesStudio.generateArticleWithAI(
      state.aiTheme,
      state.openAISettings.prompt
    );
    applyGeneratedArticle(generated);
    state.aiDialogOpen = false;
    state.aiGenerationStatus = undefined;
  } catch (error) {
    state.aiGenerationStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
  } finally {
    state.aiGenerating = false;
    render();
  }
}

async function refresh(): Promise<void> {
  state.auth = await window.lesBoulesStudio.getAuthState();
  if (state.auth.passwordRecovery || !state.auth.authenticated || !state.auth.workspaceId) {
    if (state.auth.authenticated) state.activeMenu = "site-settings";
    render();
    return;
  }
  state.config = await window.lesBoulesStudio.getConfig();
  state.openAISettings = await window.lesBoulesStudio.getOpenAISettings();
  state.categories = await window.lesBoulesStudio.listCategories();
  state.pages = await window.lesBoulesStudio.listPages();
  state.articles = await window.lesBoulesStudio.listArticles();
  state.featuredArticleSettings = await window.lesBoulesStudio.getFeaturedArticleSettings();
  state.mediaAssets = await window.lesBoulesStudio.listMediaAssets();
  state.publishConfig = await window.lesBoulesStudio.getPublishConfig();
  state.automaticPublishSettings = await window.lesBoulesStudio.getAutomaticPublishSettings();
  state.scheduledPublishJobs = await window.lesBoulesStudio.listScheduledPublishJobs();
  if (!state.selectedPublishProfileId && state.publishConfig.profiles[0]) {
    state.selectedPublishProfileId = state.publishConfig.profiles[0].id;
  }
  render();
}

function attachHandlers(): void {
  app.querySelector<HTMLButtonElement>('[data-action="settings-launcher-toggle"]')?.addEventListener("click", () => {
    state.settingsLauncherOpen = !state.settingsLauncherOpen;
    render();
  });

  app.querySelector<HTMLElement>('[data-action="settings-launcher-close"]')?.addEventListener("click", () => {
    state.settingsLauncherOpen = false;
    render();
  });

  app.querySelectorAll<HTMLSelectElement>("[data-team-role]").forEach((select) => {
    select.addEventListener("change", async () => {
      const userId = select.dataset.userId;
      if (!userId) return;
      try {
        state.teamMembers = await window.lesBoulesStudio.updateTeamMemberRole(userId, select.value as TeamRole);
        state.teamStatus = { ok: true, message: "Rôle mis à jour." };
      } catch (error) {
        state.teamStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
      }
      render();
    });
  });

  app.querySelector<HTMLSelectElement>('[data-publish-field="protocol"]')?.addEventListener("change", (event) => {
    updatePublishProfileFromForm((event.target as HTMLSelectElement).value as PublishProtocol);
    state.dirty = true;
    render();
  });

  app.querySelectorAll<HTMLButtonElement>("[data-editor-mode]").forEach((button) => {
    button.addEventListener("click", () => {
      const mode = button.dataset.editorMode === "markdown" ? "markdown" : "wysiwyg";
      applyEditorMode(mode);
    });
  });

  app.querySelectorAll<HTMLDetailsElement>(".field-help").forEach((details) => {
    details.addEventListener("toggle", () => {
      if (!details.open) {
        return;
      }
      app.querySelectorAll<HTMLDetailsElement>(".field-help").forEach((other) => {
        if (other !== details) {
          other.open = false;
        }
      });
    });
  });

  app.querySelectorAll<HTMLButtonElement>("[data-filter-category]").forEach((button) => {
    button.addEventListener("click", () => {
      state.categoryFilter = button.dataset.filterCategory || "all";
      state.statusFilter = "all";
      state.filter = "";
      state.selectedArticleId = undefined;
      state.selectedPageId = undefined;
      state.dirty = false;
      state.saveNotice = undefined;
      state.activeMenu = button.dataset.filterCategory === "all" ? "category-all" : `category-${button.dataset.filterCategory}`;
      render();
    });
  });

  app.querySelector<HTMLInputElement>("#search")?.addEventListener("input", (event) => {
    const input = event.target as HTMLInputElement;
    state.filter = input.value;
    const selectionStart = input.selectionStart ?? input.value.length;
    const selectionEnd = input.selectionEnd ?? selectionStart;
    render();
    const nextInput = app.querySelector<HTMLInputElement>("#search");
    nextInput?.focus();
    nextInput?.setSelectionRange(selectionStart, selectionEnd);
  });

  app.querySelector<HTMLSelectElement>("#article-status-filter")?.addEventListener("change", (event) => {
    state.statusFilter = (event.target as HTMLSelectElement).value;
    state.selectedArticleId = undefined;
    state.dirty = false;
    state.saveNotice = undefined;
    render();
  });

  app.querySelectorAll<HTMLButtonElement>("[data-sort-column]").forEach((button) => {
    button.addEventListener("click", () => {
      const column = button.dataset.sortColumn as ArticleSortColumn;
      if (state.sortColumn === column) {
        state.sortDirection = state.sortDirection === "asc" ? "desc" : "asc";
      } else {
        state.sortColumn = column;
        state.sortDirection = column === "date" || column === "updated" ? "desc" : "asc";
      }
      render();
    });
  });

  app.querySelectorAll<HTMLButtonElement>("[data-nav]").forEach((button) => {
    button.addEventListener("click", async () => {
      state.settingsLauncherOpen = false;
      if (button.dataset.nav === "dashboard") {
        state.activeMenu = "dashboard";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.categoryFilter = "all";
        state.statusFilter = "all";
        state.filter = "";
        state.dirty = false;
        state.saveNotice = undefined;
        render();
        return;
      }
      if (button.dataset.nav === "publication-settings") {
        state.activeMenu = "publication-settings";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.dirty = false;
        state.saveNotice = undefined;
        state.saveError = undefined;
        render();
        return;
      }
      if (button.dataset.nav === "htaccess-settings") {
        state.activeMenu = "htaccess-settings";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.dirty = false;
        state.saveNotice = undefined;
        state.saveError = undefined;
        render();
        return;
      }
      if (button.dataset.nav === "category-settings") {
        state.activeMenu = "category-settings";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.dirty = false;
        state.saveNotice = undefined;
        state.saveError = undefined;
        render();
        return;
      }
      if (button.dataset.nav === "page-settings") {
        state.activeMenu = "page-settings";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.dirty = false;
        state.saveNotice = undefined;
        state.saveError = undefined;
        render();
        return;
      }
      if (button.dataset.nav === "statistics-settings") {
        state.activeMenu = "statistics-settings";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.dirty = false;
        state.saveNotice = undefined;
        render();
        return;
      }
      if (button.dataset.nav === "openai-settings") {
        state.activeMenu = "openai-settings";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.openAIStatus = undefined;
        state.dirty = false;
        render();
        return;
      }
      if (button.dataset.nav === "google-settings") {
        state.activeMenu = "google-settings";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.dirty = false;
        state.saveNotice = undefined;
        render();
        return;
      }
      if (button.dataset.nav === "social-settings") {
        state.activeMenu = "social-settings";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.dirty = false;
        state.saveNotice = undefined;
        render();
        return;
      }
      if (button.dataset.nav === "theme-settings") {
        state.activeMenu = "theme-settings";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.dirty = false;
        state.saveNotice = undefined;
        state.saveError = undefined;
        render();
        return;
      }
      if (button.dataset.nav === "site-settings" || button.dataset.nav === "user-settings") {
        state.activeMenu = button.dataset.nav;
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.teamStatus = undefined;
        state.dirty = false;
        render();
        if (button.dataset.nav === "user-settings") await loadTeamMembers();
        return;
      }
      if (button.dataset.nav === "analytics") {
        state.activeMenu = "analytics";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.dirty = false;
        state.saveNotice = undefined;
        state.analyticsData = undefined;
        render();
        await loadAnalyticsData();
        return;
      }
      if (button.dataset.nav === "media") {
        state.activeMenu = "media";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.mediaStatus = undefined;
        state.dirty = false;
        render();
      }
    });
  });

  app.querySelector<HTMLSelectElement>("[data-push-profile-select]")?.addEventListener("change", async (event) => {
    state.selectedPublishProfileId = (event.target as HTMLSelectElement).value;
    state.pushStatus = undefined;
    state.publicationPlan = undefined;
    state.preflightReport = undefined;
    state.publicationHistory = [];
    await refreshPublishInspection();
  });

  app.querySelectorAll<HTMLInputElement>("[data-push-secret]").forEach((input) => {
    input.addEventListener("input", (event) => {
      const field = (event.target as HTMLInputElement).dataset.pushSecret;
      if (field === "password") {
        state.pushPassword = (event.target as HTMLInputElement).value;
      }
      if (field === "passphrase") {
        state.pushPassphrase = (event.target as HTMLInputElement).value;
      }
      state.pushStatus = undefined;
      app.querySelector<HTMLElement>("[data-push-operation-status]")?.remove();
      const validation = pushValidation(currentPublishProfile());
      const validationElement = app.querySelector<HTMLElement>("[data-push-validation]");
      if (validationElement) {
        validationElement.className = `status ${validation.valid ? "ok" : "warn"}`;
        validationElement.textContent = validation.valid
          ? "Configuration prête pour la mise en ligne."
          : validation.message || "Configuration incomplète.";
      }
      const planButton = app.querySelector<HTMLButtonElement>('[data-action="plan-push"]');
      if (planButton) {
        planButton.disabled = !validation.valid || state.pushRunning;
      }
      const pushButton = app.querySelector<HTMLButtonElement>('[data-action="push-now"]');
      if (pushButton) {
        pushButton.disabled = !validation.valid || !state.preflightReport?.canPublish || state.pushRunning;
      }
    });
  });

  app.querySelector<HTMLTextAreaElement>("[data-ai-theme]")?.addEventListener("input", (event) => {
    state.aiTheme = (event.target as HTMLTextAreaElement).value;
    const generateButton = app.querySelector<HTMLButtonElement>('[data-action="generate-ai-article"]');
    if (generateButton) {
      generateButton.disabled = state.aiGenerating || !state.aiTheme.trim() || !state.openAISettings.prompt.trim();
    }
  });

  app.querySelector<HTMLTextAreaElement>("[data-ai-prompt]")?.addEventListener("input", (event) => {
    state.openAISettings.prompt = (event.target as HTMLTextAreaElement).value;
    const generateButton = app.querySelector<HTMLButtonElement>('[data-action="generate-ai-article"]');
    if (generateButton) {
      generateButton.disabled = state.aiGenerating || !state.aiTheme.trim() || !state.openAISettings.prompt.trim();
    }
  });

  app.querySelectorAll<HTMLButtonElement>("[data-ai-alternative-title]").forEach((button) => {
    button.addEventListener("click", () => {
      const article = state.articles.find((item) => item.id === state.selectedArticleId);
      const title = button.dataset.aiAlternativeTitle;
      if (!article || !title) return;
      article.frontMatter.title = title;
      article.frontMatter.slug = uniqueArticleSlug(title, article.id);
      state.dirty = true;
      render();
    });
  });

  app.querySelectorAll<HTMLTableRowElement>("[data-article-id]").forEach((row) => {
    row.addEventListener("click", () => {
      state.selectedArticleId = row.dataset.articleId;
      state.selectedPageId = undefined;
      state.activeMenu = "article";
      state.dirty = false;
      state.saveNotice = undefined;
      state.articleMediaStatus = undefined;
      render();
    });
  });

  app.querySelectorAll<HTMLButtonElement>("[data-page-id]").forEach((button) => {
    button.addEventListener("click", () => {
      state.selectedPageId = button.dataset.pageId;
      state.selectedArticleId = undefined;
      state.activeMenu = `page-${button.dataset.pageId}`;
      state.dirty = false;
      state.saveNotice = undefined;
      render();
    });

    app.querySelectorAll<HTMLButtonElement>("[data-publish-profile-id]").forEach((button) => {
      button.addEventListener("click", () => {
        state.selectedPublishProfileId = button.dataset.publishProfileId;
        state.activeMenu = "publication-settings";
        state.dirty = false;
        state.saveNotice = undefined;
        render();
      });
    });
  });

  app.querySelector<HTMLInputElement>("[data-media-upload]")?.addEventListener("change", async (event) => {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (files.length === 0) return;
    state.mediaLoading = true;
    state.mediaStatus = undefined;
    render();
    try {
      for (const file of files) {
        await window.lesBoulesStudio.saveMediaAsset(file.name, new Uint8Array(await file.arrayBuffer()), file.type);
      }
      state.mediaAssets = await window.lesBoulesStudio.listMediaAssets();
      state.mediaOrphans = await window.lesBoulesStudio.findMediaOrphans();
      state.mediaStatus = { ok: true, message: `${files.length} média(s) ajouté(s).` };
    } catch (error) {
      state.mediaStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
    } finally {
      state.mediaLoading = false;
      render();
    }
  });

  app.querySelector<HTMLInputElement>("[data-article-media-upload]")?.addEventListener("change", async (event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const article = captureSelectedArticleForm();
    if (!article) return;
    state.articleMediaLoading = true;
    state.articleMediaStatus = undefined;
    render();
    try {
      const publicPath = await window.lesBoulesStudio.saveMediaAsset(
        file.name,
        new Uint8Array(await file.arrayBuffer()),
        file.type
      );
      state.mediaAssets = await window.lesBoulesStudio.listMediaAssets();
      article.frontMatter.image = publicPath;
      article.frontMatter.updated = new Date().toISOString();
      state.dirty = true;
      state.articleMediaStatus = {
        ok: true,
        message: "Image importée et sélectionnée. Enregistrez l’article pour conserver ce choix."
      };
    } catch (error) {
      state.articleMediaStatus = {
        ok: false,
        message: error instanceof Error ? error.message : String(error)
      };
    } finally {
      state.articleMediaLoading = false;
      render();
    }
  });

  app.querySelectorAll<HTMLButtonElement>("[data-action]").forEach((button) => {
    button.addEventListener("click", async () => {
      const action = button.dataset.action;
      if (!action) {
        return;
      }
      if (action === "scan-media-orphans") {
        state.mediaLoading = true;
        state.mediaStatus = undefined;
        render();
        try {
          state.mediaOrphans = await window.lesBoulesStudio.findMediaOrphans();
          state.mediaStatus = {
            ok: state.mediaOrphans.length === 0,
            message: state.mediaOrphans.length === 0
              ? "Aucun média orphelin détecté."
              : `${state.mediaOrphans.length} élément(s) orphelin(s) détecté(s).`
          };
        } catch (error) {
          state.mediaStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
        } finally {
          state.mediaLoading = false;
          render();
        }
        return;
      }
      if (action === "copy-media-path") {
        const mediaPath = button.dataset.mediaPath;
        if (mediaPath) {
          await navigator.clipboard.writeText(mediaPath);
          state.mediaStatus = { ok: true, message: "Chemin copié." };
          render();
        }
        return;
      }
      if (action === "delete-media") {
        const mediaId = button.dataset.mediaId;
        const asset = state.mediaAssets.find((candidate) => candidate.id === mediaId);
        if (asset && confirm(`Supprimer définitivement le média « ${asset.fileName} » ?`)) {
          try {
            await window.lesBoulesStudio.deleteMediaAsset(asset.id);
            state.mediaAssets = await window.lesBoulesStudio.listMediaAssets();
            state.mediaOrphans = await window.lesBoulesStudio.findMediaOrphans();
            state.mediaStatus = { ok: true, message: "Média supprimé." };
          } catch (error) {
            state.mediaStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
          }
          render();
        }
        return;
      }
      if (action === "delete-media-orphan") {
        const index = Number(button.dataset.mediaOrphanIndex);
        const orphan = state.mediaOrphans[index];
        if (orphan && confirm(`Nettoyer l’élément orphelin « ${orphan.storagePath} » ?`)) {
          try {
            await window.lesBoulesStudio.deleteMediaOrphan(orphan);
            state.mediaAssets = await window.lesBoulesStudio.listMediaAssets();
            state.mediaOrphans = await window.lesBoulesStudio.findMediaOrphans();
            state.mediaStatus = { ok: true, message: "Élément orphelin nettoyé." };
          } catch (error) {
            state.mediaStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
          }
          render();
        }
        return;
      }
      if (action === "cloud-configure") {
        const supabaseUrl = app.querySelector<HTMLInputElement>('[data-cloud-field="url"]')?.value.trim() || "";
        const supabaseAnonKey = app.querySelector<HTMLInputElement>('[data-cloud-field="anon-key"]')?.value.trim() || "";
        if (!/^https:\/\/.+\.supabase\.co\/?$/i.test(supabaseUrl) || !supabaseAnonKey) {
          state.teamStatus = { ok: false, message: "Renseignez une URL Supabase valide et sa clé publique." };
          render();
          return;
        }
        try {
          const config = await window.lesBoulesStudio.getConfig();
          await window.lesBoulesStudio.saveConfig({ ...config, supabaseUrl, supabaseAnonKey, contentMode: "supabase" });
          state.auth = await window.lesBoulesStudio.getAuthState();
          state.teamStatus = { ok: true, message: "Supabase configuré. Connectez-vous." };
        } catch (error) {
          state.teamStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
        }
        render();
        return;
      }
      if (action === "team-sign-in") {
        const email = app.querySelector<HTMLInputElement>('[data-team-field="email"]')?.value.trim() || "";
        const password = app.querySelector<HTMLInputElement>('[data-team-field="password"]')?.value || "";
        try {
          state.auth = await window.lesBoulesStudio.signIn(email, password);
          state.teamStatus = { ok: true, message: "Connexion réussie." };
          await refresh();
        } catch (error) {
          state.teamStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
          render();
        }
        return;
      }
      if (action === "open-password-reset") {
        state.passwordResetDialogOpen = true;
        state.passwordResetStatus = undefined;
        render();
        return;
      }
      if (action === "close-password-reset") {
        state.passwordResetDialogOpen = false;
        state.passwordResetStatus = undefined;
        render();
        return;
      }
      if (action === "request-password-reset") {
        const email = app.querySelector<HTMLInputElement>('[data-team-field="password-reset-email"]')?.value.trim() || "";
        if (!email || !email.includes("@")) {
          state.passwordResetStatus = { ok: false, message: "Renseignez une adresse email valide." };
          render();
          return;
        }
        try {
          await window.lesBoulesStudio.requestPasswordReset(email);
          state.passwordResetDialogOpen = false;
          state.passwordResetStatus = undefined;
          state.suppressLoginAutofill = true;
          state.teamStatus = {
            ok: true,
            message: "Si un compte correspond à cette adresse, un email de réinitialisation a été envoyé."
          };
        } catch (error) {
          state.passwordResetStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
        }
        render();
        if (!state.passwordResetDialogOpen) {
          window.setTimeout(() => {
            const emailField = app.querySelector<HTMLInputElement>('[data-team-field="email"]');
            const passwordField = app.querySelector<HTMLInputElement>('[data-team-field="password"]');
            if (emailField) emailField.value = "";
            if (passwordField) passwordField.value = "";
          }, 0);
        }
        return;
      }
      if (action === "complete-password-recovery") {
        const newPassword = app.querySelector<HTMLInputElement>('[data-team-field="recovery-password"]')?.value || "";
        const confirmation = app.querySelector<HTMLInputElement>('[data-team-field="recovery-password-confirmation"]')?.value || "";
        const validationError = newPasswordError(newPassword, confirmation);
        if (validationError) {
          state.teamStatus = { ok: false, message: validationError };
          render();
          return;
        }
        try {
          state.auth = await window.lesBoulesStudio.completePasswordRecovery(newPassword);
          state.teamStatus = { ok: true, message: "Votre mot de passe a été modifié." };
          await refresh();
        } catch (error) {
          state.teamStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
          render();
        }
        return;
      }
      if (action === "team-sign-out") {
        state.auth = await window.lesBoulesStudio.signOut();
        state.teamStatus = undefined;
        render();
        return;
      }
      if (action === "change-own-password") {
        const currentPassword = app.querySelector<HTMLInputElement>('[data-team-field="current-password"]')?.value || "";
        const newPassword = app.querySelector<HTMLInputElement>('[data-team-field="new-password"]')?.value || "";
        const confirmation = app.querySelector<HTMLInputElement>('[data-team-field="new-password-confirmation"]')?.value || "";
        const validationError = passwordChangeError(currentPassword, newPassword, confirmation);
        if (validationError) {
          state.teamStatus = { ok: false, message: validationError };
          render();
          return;
        }
        try {
          await window.lesBoulesStudio.changePassword(currentPassword, newPassword);
          state.teamStatus = {
            ok: true,
            message: "Votre mot de passe a été modifié et vos autres sessions ont été déconnectées."
          };
        } catch (error) {
          state.teamStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
        }
        render();
        return;
      }
      if (action === "team-refresh-members") {
        await loadTeamMembers();
        return;
      }
      if (action === "team-invite-member") {
        const input = {
          displayName: app.querySelector<HTMLInputElement>('[data-team-field="member-name"]')?.value.trim() || undefined,
          email: app.querySelector<HTMLInputElement>('[data-team-field="member-email"]')?.value.trim() || "",
          role: (app.querySelector<HTMLSelectElement>('[data-team-field="member-role"]')?.value || "author") as TeamRole
        };
        try {
          state.teamMembers = await window.lesBoulesStudio.inviteTeamMember(input);
          state.teamStatus = {
            ok: true,
            message: `Invitation envoyée à ${input.email}, ou accès ajouté si le compte existait déjà.`
          };
        } catch (error) {
          state.teamStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
        }
        render();
        return;
      }
      if (action === "team-send-password-reset") {
        const userId = button.dataset.userId;
        const member = state.teamMembers.find((item) => item.userId === userId);
        if (userId && member) {
          try {
            await window.lesBoulesStudio.sendTeamMemberPasswordReset(userId);
            state.teamStatus = {
              ok: true,
              message: `Un email de réinitialisation a été envoyé à ${member.email}.`
            };
          } catch (error) {
            state.teamStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
          }
          render();
        }
        return;
      }
      if (action === "team-remove-member") {
        const userId = button.dataset.userId;
        if (userId) {
          try {
            state.teamMembers = await window.lesBoulesStudio.removeTeamMember(userId);
            state.teamStatus = { ok: true, message: "Accès au site retiré." };
          } catch (error) {
            state.teamStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
          }
          render();
        }
        return;
      }
      if (action === "team-site-creation-toggle") {
        state.siteCreationOpen = !state.siteCreationOpen;
        render();
        return;
      }
      if (action === "team-update-workspace") {
        const name = app.querySelector<HTMLInputElement>('[data-team-field="site-name"]')?.value.trim() || "";
        const slug = app.querySelector<HTMLInputElement>('[data-team-field="site-slug"]')?.value.trim() || "";
        try {
          await window.lesBoulesStudio.updateWorkspace(name, slug);
          state.auth = await window.lesBoulesStudio.getAuthState();
          state.teamStatus = { ok: true, message: "Informations du site enregistrées." };
        } catch (error) {
          state.teamStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
        }
        render();
        return;
      }
      if (action === "team-create-workspace") {
        const name = app.querySelector<HTMLInputElement>('[data-team-field="workspace-name"]')?.value.trim() || "";
        const slug = app.querySelector<HTMLInputElement>('[data-team-field="workspace-slug"]')?.value.trim() || "";
        try {
          const workspaceId = await window.lesBoulesStudio.createWorkspace(name, slug);
          state.auth = await window.lesBoulesStudio.selectWorkspace(workspaceId);
          state.teamStatus = { ok: true, message: "Site créé et activé." };
          await refresh();
          state.activeMenu = "site-settings";
          render();
        } catch (error) {
          state.teamStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
          render();
        }
        return;
      }
      if (action === "team-select-workspace") {
        const workspaceId = button.dataset.workspaceId;
        if (workspaceId) {
          state.auth = await window.lesBoulesStudio.selectWorkspace(workspaceId);
          state.teamStatus = { ok: true, message: "Site activé." };
          await refresh();
          state.activeMenu = "site-settings";
          render();
        }
        return;
      }
      if (action === "sidebar-toggle") {
        state.sidebarCollapsed = !state.sidebarCollapsed;
        render();
        return;
      }
      if (action === "article-list-toggle") {
        state.articleListCollapsed = !state.articleListCollapsed;
        render();
        return;
      }
      if (action === "article-editor-toggle") {
        state.articleEditorCollapsed = !state.articleEditorCollapsed;
        render();
        return;
      }
      if (action === "open-ai-dialog") {
        if (!state.openAISettings.configured) {
          state.activeMenu = "openai-settings";
          state.openAIStatus = { ok: false, message: "Configurez une clé API OpenAI avant de générer un article." };
          state.settingsLauncherOpen = false;
          render();
          return;
        }
        state.aiDialogOpen = true;
        state.aiTheme = "";
        state.aiGenerationStatus = undefined;
        render();
        return;
      }
      if (action === "close-ai-dialog") {
        if (!state.aiGenerating) {
          state.aiDialogOpen = false;
          state.aiGenerationStatus = undefined;
          render();
        }
        return;
      }
      if (action === "generate-ai-article") {
        await generateCurrentArticleWithAI();
        return;
      }
      if (action === "reset-ai-prompt") {
        state.openAISettings.prompt = state.openAISettings.defaultPrompt;
        state.aiGenerationStatus = undefined;
        render();
        return;
      }
      if (action === "open-push-dialog") {
        if (!state.selectedPublishProfileId && state.publishConfig.profiles[0]) {
          state.selectedPublishProfileId = state.publishConfig.profiles[0].id;
        }
        state.pushPassword = "";
        state.pushPassphrase = "";
        state.pushStatus = undefined;
        state.publicationPlan = undefined;
        state.preflightReport = undefined;
        state.publicationHistory = [];
        state.pushDialogOpen = true;
        render();
        await refreshPublishInspection();
        return;
      }
      if (action === "close-push-dialog") {
        state.pushDialogOpen = false;
        state.pushRunning = false;
        state.pushStatus = undefined;
        render();
        return;
      }
      if (action === "push-now") {
        await pushCurrentSite();
        return;
      }
      if (action === "plan-push") {
        await refreshPublishInspection(true);
        return;
      }
      if (action === "restore-publication") {
        const publicationId = (target as HTMLElement).dataset.publicationId;
        if (publicationId) await restoreSelectedPublication(publicationId);
        return;
      }
      if (action === "automatic-publish-save") {
        await saveAutomaticPublishing();
        return;
      }
      if (action === "automatic-publish-delete-secret") {
        await deleteAutomaticPublishingSecret();
        return;
      }
      if (action === "automatic-publish-retry") {
        const jobId = (target as HTMLElement).dataset.jobId;
        if (jobId) await retryAutomaticPublishJob(jobId);
        return;
      }
      if (action === "new-article") {
        state.activeMenu = "new-article";
        state.selectedPageId = undefined;
        state.selectedArticleId = undefined;
        state.categoryFilter = "all";
        state.statusFilter = "all";
        state.filter = "";
        state.aiGeneratedArticleId = undefined;
        state.aiGenerationNote = undefined;
        state.aiAlternativeTitles = [];
        state.articleMediaStatus = undefined;
        const article = await window.lesBoulesStudio.createArticle();
        state.articles.unshift(article);
        state.selectedArticleId = article.id;
        state.dirty = false;
        render();
        return;
      }
      if (action === "new-page") {
        try {
          const page = await window.lesBoulesStudio.createPage();
          state.pages.push(page);
          state.selectedArticleId = undefined;
          state.selectedPageId = page.id;
          state.activeMenu = `page-${page.id}`;
          state.dirty = false;
          state.saveError = undefined;
          render();
        } catch (error) {
          state.saveError = error instanceof Error ? error.message : String(error);
          render();
        }
        return;
      }
      if (action === "refresh") {
        state.progressNotice = {
          title: "Rafraîchissement",
          caption: "Rafraîchissement en cours. Retour a la vue generale dans un instant.",
          durationMs: 5000
        };
        render();
        await new Promise((resolve) => window.setTimeout(resolve, 5000));
        state.progressNotice = undefined;
        state.activeMenu = "dashboard";
        state.selectedArticleId = undefined;
        state.selectedPageId = undefined;
        state.dirty = false;
        state.saveNotice = undefined;
        await refresh();
        return;
      }
      if (action === "clear-search") {
        state.filter = "";
        render();
        app.querySelector<HTMLInputElement>("#search")?.focus();
        return;
      }
      if (action === "save") {
        await saveSelectedArticle();
        return;
      }
      if (action === "save-featured-article") {
        if (state.selectedArticleId) await saveFeaturedArticle(state.selectedArticleId);
        return;
      }
      if (action === "remove-featured-article") {
        await saveFeaturedArticle();
        return;
      }
      if (action === "save-page") {
        await saveSelectedPage();
        return;
      }
      if (action === "save-page-summary") {
        const editor = button.closest<HTMLElement>("[data-page-editor]");
        const pageId = editor?.dataset.pageEditorId;
        const page = state.pages.find((item) => item.id === pageId);
        if (!editor || !page) return;
        const title = editor.querySelector<HTMLInputElement>('[data-page-summary-field="title"]')?.value.trim() || "";
        const slug = editor.querySelector<HTMLInputElement>('[data-page-summary-field="slug"]')?.value.trim() || slugifyTitle(title);
        const orderValue = editor.querySelector<HTMLInputElement>('[data-page-summary-field="order"]')?.value || "";
        if (!title || !slug) {
          state.saveError = "Le titre et le slug de la page sont obligatoires.";
          render();
          return;
        }
        try {
          const saved = await window.lesBoulesStudio.savePage({
            ...page,
            frontMatter: {
              ...page.frontMatter,
              title,
              slug,
              order: orderValue ? Number(orderValue) : undefined
            }
          });
          state.pages = state.pages.map((item) => item === page ? saved : item);
          state.saveError = undefined;
          showSaveNotice("Page enregistrée");
          render();
        } catch (error) {
          state.saveError = error instanceof Error ? error.message : String(error);
          render();
        }
        return;
      }
      if (action === "edit-page") {
        const pageId = button.closest<HTMLElement>("[data-page-editor]")?.dataset.pageEditorId;
        if (pageId) {
          state.selectedArticleId = undefined;
          state.selectedPageId = pageId;
          state.activeMenu = `page-${pageId}`;
          state.saveError = undefined;
          state.saveNotice = undefined;
          render();
        }
        return;
      }
      if (action === "delete-page-summary") {
        const pageId = button.closest<HTMLElement>("[data-page-editor]")?.dataset.pageEditorId;
        const page = state.pages.find((item) => item.id === pageId);
        if (page && confirm(`Supprimer la page « ${page.frontMatter.title} » ?`)) {
          try {
            await window.lesBoulesStudio.deletePage(page.id);
            state.pages = state.pages.filter((item) => item !== page);
            state.saveError = undefined;
            showSaveNotice("Page supprimée");
            render();
          } catch (error) {
            state.saveError = error instanceof Error ? error.message : String(error);
            render();
          }
        }
        return;
      }
      if (action === "delete-page") {
        const page = sortPages(state.pages).find((item) => item.id === state.selectedPageId);
        if (page && confirm(`Supprimer la page « ${page.frontMatter.title} » ?`)) {
          try {
            await window.lesBoulesStudio.deletePage(page.id);
            state.selectedPageId = undefined;
            state.activeMenu = "page-settings";
            state.saveError = undefined;
            await refresh();
          } catch (error) {
            state.saveError = error instanceof Error ? error.message : String(error);
            render();
          }
        }
        return;
      }
      if (action === "save-category") {
        const editor = button.closest<HTMLElement>("[data-category-editor]");
        if (!editor) return;
        const name = editor.querySelector<HTMLInputElement>('[data-category-field="name"]')?.value.trim() || "";
        const slug = editor.querySelector<HTMLInputElement>('[data-category-field="slug"]')?.value.trim() || slugifyTitle(name);
        const orderValue = editor.querySelector<HTMLInputElement>('[data-category-field="order"]')?.value || "0";
        const category: Category = {
          id: editor.dataset.categoryId || createCategoryId(),
          name,
          slug,
          order: Number(orderValue) || 0,
          enabled: editor.querySelector<HTMLInputElement>('[data-category-field="enabled"]')?.checked ?? true
        };
        try {
          state.categories = await window.lesBoulesStudio.saveCategory(category);
          state.saveError = undefined;
          showSaveNotice(editor.dataset.categoryId ? "Catégorie enregistrée" : "Catégorie ajoutée");
          render();
        } catch (error) {
          state.saveError = error instanceof Error ? error.message : String(error);
          render();
        }
        return;
      }
      if (action === "delete-category") {
        const editor = button.closest<HTMLElement>("[data-category-editor]");
        const id = editor?.dataset.categoryId;
        const name = editor?.querySelector<HTMLInputElement>('[data-category-field="name"]')?.value.trim() || "cette catégorie";
        if (id && confirm(`Archiver la catégorie « ${name} » ?`)) {
          try {
            state.categories = await window.lesBoulesStudio.deleteCategory(id);
            state.saveError = undefined;
            showSaveNotice("Catégorie archivée");
            render();
          } catch (error) {
            state.saveError = error instanceof Error ? error.message : String(error);
            render();
          }
        }
        return;
      }
      if (action === "markdown-preview") {
        await openMarkdownPreview();
        return;
      }
      if (action === "publish-save") {
        await savePublishSettings();
        return;
      }
      if (action === "htaccess-save") {
        await saveHtaccessSettings();
        return;
      }
      if (action === "publish-add-profile") {
        await addPublishProfile(false);
        return;
      }
      if (action === "publish-delete-profile") {
        await deleteCurrentPublishProfile();
        return;
      }
      if (action === "publish-new-from-current") {
        await addPublishProfile(true);
        return;
      }
      if (action === "site-settings-save") {
        await saveSiteSettings();
        return;
      }
      if (action === "theme-delete") {
        await deleteTheme(button.dataset.themeId || "");
        return;
      }
      if (action === "openai-settings-save") {
        const apiKey = app.querySelector<HTMLInputElement>('[data-openai-field="apiKey"]')?.value.trim();
        const model = app.querySelector<HTMLInputElement>('[data-openai-field="model"]')?.value.trim() || "gpt-4.1-mini";
        try {
          state.openAISettings = await window.lesBoulesStudio.saveOpenAISettings({ apiKey: apiKey || undefined, model });
          state.openAIStatus = { ok: true, message: "Configuration OpenAI enregistrée sur cet appareil." };
        } catch (error) {
          state.openAIStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
        }
        render();
        return;
      }
      if (action === "openai-settings-clear") {
        try {
          state.openAISettings = await window.lesBoulesStudio.saveOpenAISettings({ clearApiKey: true, model: state.openAISettings.model });
          state.openAIStatus = { ok: true, message: "Clé OpenAI supprimée de cet appareil." };
        } catch (error) {
          state.openAIStatus = { ok: false, message: error instanceof Error ? error.message : String(error) };
        }
        render();
        return;
      }
      if (action === "test-supabase-connection") {
        await testCurrentSupabaseConnection();
        return;
      }
      if (action === "open-supabase") {
        await openCurrentSupabase();
        return;
      }
      if (action === "social-select-default") {
        const socialSharingEnabled = getSiteFieldValue("socialSharingEnabled") === "true";
        app.querySelectorAll<HTMLInputElement>("[data-social-network]").forEach((input) => {
          input.checked = DEFAULT_SOCIAL_NETWORKS.includes(input.dataset.socialNetwork || "");
        });
        if (state.config) {
          state.config.socialSharingEnabled = socialSharingEnabled;
          state.config.socialNetworks = [...DEFAULT_SOCIAL_NETWORKS];
        }
        state.dirty = true;
        state.saveNotice = undefined;
        render();
        return;
      }
      if (action === "social-select-all") {
        const socialSharingEnabled = getSiteFieldValue("socialSharingEnabled") === "true";
        const allNetworks = SOCIAL_NETWORKS.map((network) => network.id);
        app.querySelectorAll<HTMLInputElement>("[data-social-network]").forEach((input) => {
          input.checked = true;
        });
        if (state.config) {
          state.config.socialSharingEnabled = socialSharingEnabled;
          state.config.socialNetworks = allNetworks;
        }
        state.dirty = true;
        state.saveNotice = undefined;
        render();
        return;
      }
      if (action === "social-clear-all") {
        const socialSharingEnabled = getSiteFieldValue("socialSharingEnabled") === "true";
        app.querySelectorAll<HTMLInputElement>("[data-social-network]").forEach((input) => {
          input.checked = false;
        });
        if (state.config) {
          state.config.socialSharingEnabled = socialSharingEnabled;
          state.config.socialNetworks = [];
        }
        state.dirty = true;
        state.saveNotice = undefined;
        render();
        return;
      }
      if (action === "build") {
        state.buildStatus = "Génération en cours…";
        state.progressNotice = {
          title: "Generation du site",
          caption: "Generation du site en cours. Merci de patienter quelques secondes.",
          durationMs: 5000
        };
        render();
        const minimumDelay = new Promise((resolve) => window.setTimeout(resolve, 5000));
        try {
          const [report] = await Promise.all([window.lesBoulesStudio.buildSite(), minimumDelay]);
          state.buildStatus = `Généré: ${JSON.stringify(report)}`;
        } finally {
          state.progressNotice = undefined;
          render();
        }
        return;
      }
      if (action === "preview" || action === "start-preview") {
        state.activeMenu = "start-preview";
        state.previewUrl = await window.lesBoulesStudio.startPreview();
        render();
        return;
      }
      if (action === "duplicate" && state.selectedArticleId) {
        const article = await window.lesBoulesStudio.duplicateArticle(state.selectedArticleId);
        state.articles.unshift(article);
        state.selectedArticleId = article.id;
        state.articleMediaStatus = undefined;
        render();
        return;
      }
      if (action === "archive" && state.selectedArticleId) {
        await window.lesBoulesStudio.archiveArticle(state.selectedArticleId);
        await refresh();
        return;
      }
      if (action === "delete" && state.selectedArticleId) {
        if (confirm("Supprimer cet article ?")) {
          await window.lesBoulesStudio.deleteArticle(state.selectedArticleId);
          state.selectedArticleId = undefined;
          await refresh();
        }
      }
      if (action === "refresh-analytics") {
        await loadAnalyticsData();
        return;
      }
      if (action === "export-analytics-csv") {
        const events = filterAnalyticsEvents(state.analyticsEvents, state.analyticsRange, state.analyticsQuery);
        downloadAnalyticsCsv(events);
        return;
      }
    });
  });

  app.querySelector<HTMLSelectElement>("[data-analytics-range]")?.addEventListener("change", (event) => {
    state.analyticsRange = (event.target as HTMLSelectElement).value as AnalyticsRange;
    render();
    rerenderAnalyticsChart();
  });

  app.querySelector<HTMLInputElement>("[data-analytics-search]")?.addEventListener("input", (event) => {
    state.analyticsQuery = (event.target as HTMLInputElement).value;
    render();
    rerenderAnalyticsChart();
  });

  app.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-field]").forEach((element) => {
    element.addEventListener("input", () => {
      state.dirty = true;
      state.saveNotice = undefined;
    });
    element.addEventListener("change", () => {
      state.dirty = true;
      state.saveNotice = undefined;
    });
  });

  app.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-page-field]").forEach((element) => {
    element.addEventListener("input", () => {
      if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
        updateCharacterCount(element);
      }
      state.dirty = true;
      state.saveNotice = undefined;
    });
    element.addEventListener("change", () => {
      state.dirty = true;
      state.saveNotice = undefined;
    });
  });

  app.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-publish-field]").forEach((element) => {
    const syncPublishSetting = (): void => {
      if (element.dataset.publishField === "htaccessEnabled" && element instanceof HTMLInputElement) {
        state.publishConfig.htaccessEnabled = element.checked;
      }
      if (element.dataset.publishField === "htaccessContent") {
        state.publishConfig.htaccessContent = element.value;
      }
      if (element.dataset.publishField !== "htaccessEnabled" && element.dataset.publishField !== "htaccessContent") {
        updatePublishProfileFromForm(getPublishFieldValue("protocol") as PublishProtocol);
      }
      state.dirty = true;
      state.saveNotice = undefined;
      state.saveError = undefined;
    };
    element.addEventListener("input", syncPublishSetting);
    element.addEventListener("change", syncPublishSetting);
  });

  app.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-site-field]").forEach((element) => {
    element.addEventListener("input", () => {
      if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
        updateCharacterCount(element);
      }
      state.dirty = true;
      state.saveNotice = undefined;
      state.supabaseConnectionStatus = undefined;
    });
    element.addEventListener("change", () => {
      state.dirty = true;
      state.saveNotice = undefined;
      state.supabaseConnectionStatus = undefined;
    });
  });

  app.querySelectorAll<HTMLInputElement>("[data-social-network]").forEach((element) => {
    element.addEventListener("change", () => {
      if (state.config) {
        state.config.socialNetworks = Array.from(app.querySelectorAll<HTMLInputElement>("[data-social-network]:checked"))
          .map((input) => input.dataset.socialNetwork || "")
          .filter(Boolean);
      }
      state.dirty = true;
      state.saveNotice = undefined;
      render();
    });
  });

  app.querySelector<HTMLInputElement>('[data-site-field="socialSharingEnabled"]')?.addEventListener("change", (event) => {
    if (state.config) {
      state.config.socialSharingEnabled = (event.target as HTMLInputElement).checked;
    }
  });

  app.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[data-field]").forEach((field) => {
    if (field instanceof HTMLInputElement && field.type === "checkbox") {
      return;
    }
    field.addEventListener("input", () => {
      if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) {
        updateCharacterCount(field);
      }
      if (field.dataset.field === "title") {
        const slugField = app.querySelector<HTMLInputElement>('[data-field="slug"]');
        if (slugField) {
          slugField.value = slugifyTitle(field.value);
        }
      }
      updateArticleFieldValidation();
      if (state.selectedArticleId) {
        const selected = state.articles.find((article) => article.id === state.selectedArticleId);
        if (selected) {
          updateSelectedArticle({});
        }
      }
    });
    field.addEventListener("change", () => {
      updateArticleFieldValidation();
      if (field.dataset.field === "image") {
        captureSelectedArticleForm();
        state.articleMediaStatus = undefined;
        render();
      }
    });
  });

  app.querySelectorAll<HTMLInputElement>("[data-featured-field]").forEach((field) => {
    field.addEventListener("input", () => {
      if (!state.selectedArticleId) return;
      state.featuredArticleDraft = {
        articleId: state.selectedArticleId,
        startsAt: app.querySelector<HTMLInputElement>('[data-featured-field="startsAt"]')?.value || "",
        endsAt: app.querySelector<HTMLInputElement>('[data-featured-field="endsAt"]')?.value || ""
      };
    });
  });
}

window.lesBoulesStudio.onContentChanged(() => {
  if (state.dirty || !state.auth?.workspaceId) return;
  if (realtimeRefreshTimer) window.clearTimeout(realtimeRefreshTimer);
  realtimeRefreshTimer = window.setTimeout(() => {
    realtimeRefreshTimer = undefined;
    void refresh();
  }, 250);
});

void refresh();
